// ════════════════════════════════════════════════════════════════════════════
// GREEN CARD EFFECTS
// See js/effects/registry.js for the registerCardEffect() API and the list
// of available triggers.
// ════════════════════════════════════════════════════════════════════════════

// ─── EB01-077 · Master League Begins ─────────────────────────────────────
// [Burst] Add this card to your hand — handled generically (literal
// "[Burst]Add this card to your hand." pattern, see registry.js).
// [Action] Choose 1 rested friendly (G Generation) Unit. Change a battling
// enemy Unit's attack target to it.
// The defender override is applied in runDamageStep (game.js) via
// state.pendingDefenderOverride, and synced to the attacker client via
// CHANGE_ATTACK_TARGET (network.js remoteHandlers). Same convention as
// ST07-013 Armed Intervention.
function eb01077Candidates() {
  return state.playerField.filter(c => c && c.rested && (c.trait || '').includes('(G Generation)'));
}
registerCardEffect('EB01-077', {
  canActivate: (card, ctx) => {
    // Only usable during the Action Step when an enemy Unit is attacking
    if (!ctx || ctx.attackerSide !== 'opp') return false;
    return eb01077Candidates().length > 0;
  },
  action: (card, ctx) => {
    const candidates = eb01077Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested friendly (G Generation) Units. No effect.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested friendly (G Generation) Unit. The enemy attack is redirected to it.', candidates, (target) => {
      if (target) {
        ctx.battle.defender = target;
        ctx.battle.isShield = false;
        ctx.battle.isDirect = false;
        NET.send({ type: 'CHANGE_ATTACK_TARGET', uid: target.uid });
        addLog(`${card.name} — attack redirected to ${target.name}.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD01-126 · Underground Desert Base ──────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// No additional effect; no registration needed.

// ─── GD01-125 · Zanzibar ─────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if it is the player's turn, they may deploy 1 (Zeon) Unit card that
// is Lv.4 or lower from their hand for free.
registerCardEffect('GD01-125', {
  deploy: (card) => {
    if (!state.myTurn) return;
    if (findNextFieldSlot() === -1) return;
    const candidates = state.hand.filter(c =>
      c && c.type === 'unit' &&
      (c.trait || '').includes('(Zeon)') &&
      (parseInt(c.level) || 0) <= 4
    );
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'You may deploy 1 (Zeon) Unit card that is Lv.4 or lower from your hand.',
      candidates,
      (chosen) => {
        if (!chosen) return;
        const slot = findNextFieldSlot();
        if (slot === -1) {
          addLog(`${card.name} — field became full. No effect.`);
          return;
        }
        state.hand = state.hand.filter(c => c.uid !== chosen.uid);
        state.playerField[slot] = chosen;
        chosen.tempCantAttack = true;
        updateTempState(chosen.cardNo, chosen.copyVer, 'field');
        addLog(`${card.name} — deployed ${chosen.name} from hand.`);
        triggerCardEffect(chosen, 'deploy', { slot });
        const trait = chosen.trait || '';
        if (trait.includes('Superpower Bloc') || trait.includes('(UN)')) {
          state.superpowerDeployedThisTurn = true;
        }
        NET.send({ type: 'PLAY_CARD', slot, card: chosen, counters: myCounters() });
        render();
        setupDropZones();
      },
      card,
      { mandatory: false }
    );
  },
});

// ─── ST02-015 · Saint Gabriel Institute ──────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if it is the player's turn, look at the top 2 cards of the deck.
// Choose 1 to place on top; the other goes to the bottom.
registerCardEffect('ST02-015', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const peeked = peekTopOfDeck(2);
    if (peeked.length < 2) {
      addLog(`${card.name} — fewer than 2 cards in deck. No rearrangement.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose which card to place on top of your deck (the other goes to the bottom).',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) return;
        const chosenPeek  = peeked.find(p => p.card.uid === chosen.uid);
        const otherPeek   = peeked.find(p => p.card.uid !== chosen.uid);
        // Send the "other" to the bottom first, then the chosen to the top.
        if (otherPeek)  sendDeckEntryToBottom(otherPeek.entry);
        if (chosenPeek) sendDeckEntryToTop(chosenPeek.entry);
        addLog(`${card.name} — rearranged top 2 deck cards.`);
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST03-016 · Falmel ───────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if it is the player's turn, deploy 1 Char's Zaku II (T-006) token
// in a rested state.
registerCardEffect('ST03-016', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const token = deployTokenToField('T-006');
    if (!token) {
      addLog(`${card.name} — no empty field slot for Char's Zaku II token.`);
      return;
    }
    setRested(token, true);
    addLog(`${card.name} — Char's Zaku II token deployed rested.`);
  },
});

// ─── GD02-123 · Sodon ────────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if it is the player's turn, choose 1 friendly Token Unit. Until end
// of this turn it can attack active Units whose current AP is 5 or less.
registerCardEffect('GD02-123', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const tokens = state.playerField.filter(c => c && isUnitToken(c));
    if (tokens.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your Token Units. Until end of turn it can attack active Units with 5 or less AP.',
      tokens,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('CanAttackActiveAP5');
        addLog(`${card.name} — ${target.name} can attack active Units with AP 5 or less this turn.`);
        if (state.networked) NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP5' });
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST06-015 · Kaneban Co., Ltd. ────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Once per Turn] During your turn, when a friendly (Clan) Unit satisfies its
// Link requirement, it gains <Breach 3> during this turn.
// Fires via the onPlayerUnitLinked hook in game.js PAIR_PILOT.
registerCardEffect('ST06-015', {
  onPlayerUnitLinked: (base, unit, slot) => {
    if (!state.myTurn) return;
    if (base.passiveUsedThisTurn) return;
    if (!(unit.trait || '').includes('(Clan)')) return;
    base.passiveUsedThisTurn = true;
    unit.tempKeywords = unit.tempKeywords || [];
    unit.tempKeywords.push('Breach 3');
    addLog(`${base.name} — ${unit.name} linked with (Clan) trait; gains Breach 3 this turn.`);
    if (state.networked) NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'Breach 3' });
    render();
  },
});

// ─── GD02-124 · Diva ─────────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Passive: during your turn, if your level is 7 or more, all friendly green
// (Earth Federation) Units gain AP+1.
// Applies via getBaseUnitApBonus() called inside effectiveBp().
registerCardEffect('GD02-124', {
  baseUnitApBonus: (target, ownerSide) => {
    const isMySide   = ownerSide === 'player';
    const isMyTurn   = isMySide ? state.myTurn : !state.myTurn;
    if (!isMyTurn) return 0;
    const ownerLevel = isMySide ? (state.resBase + state.exRes + state.exResRested) : state.oppLevel;
    if (ownerLevel < 7) return 0;
    if ((target.color || '').toLowerCase() !== 'green') return 0;
    return 1;
  },
});

// ─── GD03-126 · Cyclops Team ─────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Passive: during your opponent's turn, all of your Token Units gain AP+1.
// Applies via getBaseUnitApBonus() called inside effectiveBp().
registerCardEffect('GD03-126', {
  baseUnitApBonus: (target, ownerSide) => {
    const isMySide = ownerSide === 'player';
    const isMyTurn = isMySide ? state.myTurn : !state.myTurn;
    if (isMyTurn) return 0; // bonus only applies during the opponent's turn
    if (!isUnitToken(target)) return 0;
    return 1;
  },
});

// ─── GD03-125 · Peacemillion ──────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Once per Turn] During your turn, when a friendly (Operation Meteor) or
// (G Team) Unit that is Lv.6 or higher destroys an enemy Unit with battle
// damage while attacking, that friendly Unit may recover 2 HP.
// Fires via the onPlayerUnitDestroysByBattle hook in notifyUnitDestroyedByBattle(),
// which runDamageStep calls the INSTANT the defender is confirmed destroyed —
// still well before the attacker's own counter-damage from the same
// exchange is applied a bit further down that same function. Checking HP
// immediately therefore always saw the destroyer's pre-counter-damage
// total, i.e. "still full from before this battle", making "already at max
// HP" true for almost every attacker and defeating the whole point of an
// ability meant to offset the damage it just took destroying something.
// Deferred one tick (setTimeout 0) so the rest of that synchronous damage
// step — including the counter-damage — finishes first; the HP check then
// reflects the real post-battle state.
registerCardEffect('GD03-125', {
  onPlayerUnitDestroysByBattle: (base, destroyer, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (!state.myTurn) return;
    if (base.passiveUsedThisTurn) return;
    const trait = destroyer.trait || '';
    if (!trait.includes('(Operation Meteor)') && !trait.includes('(G Team)')) return;
    if ((parseInt(destroyer.level) || 0) < 6) return;
    setTimeout(() => {
      // The destroyer may have left the field entirely by now (e.g. it also
      // died to counter-damage in the same exchange) — nothing to recover.
      if (!state.playerField.some(c => c && c.uid === destroyer.uid)) return;
      const curHp = effectiveHp(destroyer);
      const maxHp = effectiveMaxHp(destroyer);
      if (curHp >= maxHp) {
        addLog(`${base.name} — ${destroyer.name} is already at max HP. No recovery.`);
        return;
      }
      base.passiveUsedThisTurn = true;
      showCardPickerModal(
        base.name,
        `${destroyer.name} destroyed an enemy! It may recover 2 HP. Click it to confirm, or skip.`,
        [destroyer],
        (target) => {
          if (!target) return;
          applyHpMod([target], 2);
          addLog(`${base.name} — ${target.name} recovered 2 HP (HP now: ${effectiveHp(target)}).`);
          render();
        },
        base,
        { mandatory: false }
      );
    }, 0);
  },
});

// ─── EB01-087 · Marina Ismail & Ptolemaios 2 ─────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// [Once per Turn] During your turn, when a friendly green (G Generation) Unit
// destroys an enemy Unit with battle damage, choose 1 friendly Unit. It
// recovers 2 HP.
// Fires via the onPlayerUnitDestroysByBattle hook in notifyUnitDestroyedByBattle().
registerCardEffect('EB01-087', {
  onPlayerUnitDestroysByBattle: (base, destroyer, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (!state.myTurn) return;
    if (base.passiveUsedThisTurn) return;
    if ((destroyer.color || '').toLowerCase() !== 'green') return;
    if (!(destroyer.trait || '').includes('(G Generation)')) return;
    const candidates = state.playerField.filter(c => c != null);
    if (candidates.length === 0) return;
    base.passiveUsedThisTurn = true;
    showCardPickerModal(
      base.name,
      'Choose 1 friendly Unit. It recovers 2 HP.',
      candidates,
      (target) => {
        if (!target) return;
        applyHpMod([target], 2);
        addLog(`${base.name} — ${target.name} recovers 2 HP (now: ${effectiveHp(target)}).`);
        render();
        setupDropZones();
      },
      base,
      { mandatory: true }
    );
  },
});

// ─── EB01-088 · Miorine Rembran & Academy Ship ───────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Passive: all friendly (G Generation) Units that are Lv.3 get AP+1 during
// your opponent's turn.
// Applies via getBaseUnitApBonus() called inside effectiveBp().
registerCardEffect('EB01-088', {
  baseUnitApBonus: (target, ownerSide) => {
    const isMySide = ownerSide === 'player';
    const isMyTurn = isMySide ? state.myTurn : !state.myTurn;
    if (isMyTurn) return 0; // bonus only applies during the opponent's turn
    if (!(target.trait || '').includes('(G Generation)')) return 0;
    if ((parseInt(target.level) || 0) !== 3) return 0;
    return 1;
  },
});

// ─── GD04-124 · 9th Tactical Testing Sector ──────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// When you place an EX Resource during your turn, choose 1 friendly (Academy)
// Unit. It gets AP+2 during this turn.
// Fires via placeExResource() in game.js calling the onExResourcePlaced hook.
registerCardEffect('GD04-124', {
  onExResourcePlaced: (card, opts) => {
    if (!state.myTurn) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Academy)'));
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Academy) Units. It gets AP+2 during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempApMod = (target.tempApMod || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-123 · A Baoa Qu ────────────────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Passive: this Base cannot receive battle damage from Units that are Lv.4 or
// lower while a rested (Zeon) Unit is in your field. Breach and effect damage
// still apply normally. Counter-damage to the attacker is unaffected.
// Applies via the isImmuneToBattleDamageFrom hook in runDamageStep().
registerCardEffect('GD04-123', {
  isImmuneToBattleDamageFrom: (base, attacker, defenderSide) => {
    if ((parseInt(attacker.level) || 0) > 4) return false;
    const defField = defenderSide === 'player' ? state.playerField : state.oppField;
    return defField.some(c => c && c.rested && (c.trait || '').includes('(Zeon)'));
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GREEN COMMAND & COMMAND PILOT CARD EFFECTS
// ════════════════════════════════════════════════════════════════════════════

// ─── Shared: "all players look at the top card of their own deck; if it
// qualifies they may reveal it and add it to hand; otherwise/declined →
// choose top or bottom" mechanic (e.g. EB01-078 Premium Unit Assembly,
// EB01-023 Le Cygne). Each side resolves this against their OWN deck
// independently — the activating client peeks/decides for itself, then
// sends FORCE_LOOK_TOP_CARD_OR_RETURN so the opponent's client runs the
// identical local resolution against their own tempDeck/hand (same "each
// side handles their own state" convention as FORCE_DRAW in network.js,
// e.g. GD04-128 Armory One). `qualifies(peekedCard)` decides whether the
// "Add to Hand" option is offered; `qualifyLabel` describes the condition
// in the modal text (e.g. "Unit", "Lv.5 or higher card").
// Same card-image + stats presentation as showTopOrBottomModal/
// showTopOrTrashModal/showTopDeckPeekPrompt above (see their comments for
// why) — this one additionally offers "Add to Hand" when the peeked card
// qualifies.
function showLookTopCardPeekModal(sourceName, peeked, entry, onDone, qualifies, qualifyLabel) {
  const doesQualify = qualifies(peeked);

  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const row = (label, val) =>
    `<div class="cp-row">
       <span class="cp-label">${label}</span>
       <span class="cp-val">${val ?? '—'}</span>
     </div>`;

  const promptText = doesQualify
    ? `${sourceName} — ${peeked.name} is a ${qualifyLabel}. Reveal it and add it to your hand, or return it to the top or bottom of your deck.`
    : `${sourceName} — ${peeked.name} isn't a ${qualifyLabel}. Return it to the top or bottom of your deck.`;

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
    <div class="igm-title">Top of Deck</div>
    <div class="srb-content">
      <div class="cp-img-wrap">
        ${peeked.img ? `<img src="${peeked.img}" alt="${peeked.name}" />` : '<div class="cp-no-img">?</div>'}
      </div>
      <div class="cp-stats">
        <div class="cp-name">${peeked.name}</div>
        ${peeked.trait ? `<div class="cp-trait">${peeked.trait}</div>` : ''}
        <div class="cp-divider"></div>
        <div class="cp-stat-grid">
          ${row('Level', peeked.level)}
          ${row('Cost',  peeked.cost)}
          ${row('AP',    peeked.bp)}
          ${row('HP',    peeked.hp)}
          ${row('Type',  displayCardType(peeked.type))}
        </div>
        <div class="cp-effect">${peeked.effect ?? '—'}</div>
      </div>
    </div>
    <div class="igm-body">${promptText}</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-return-top">Return to Top</button>
      <button class="igm-btn igm-cancel">Return to Bottom</button>
      ${doesQualify ? '<button class="igm-btn igm-confirm-base">Add to Hand</button>' : ''}
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });

  function finish() { overlay.remove(); onDone?.(); }

  modal.querySelector('.igm-return-top').addEventListener('click', () => {
    sendDeckEntryToTop(entry);
    addLog(`${sourceName} — ${peeked.name} returned to the top of the deck.`);
    render(); setupDropZones();
    finish();
  });
  modal.querySelector('.igm-cancel').addEventListener('click', () => {
    sendDeckEntryToBottom(entry);
    addLog(`${sourceName} — ${peeked.name} returned to the bottom of the deck.`);
    render(); setupDropZones();
    finish();
  });
  modal.querySelector('.igm-confirm-base')?.addEventListener('click', () => {
    entry.state = 'hand';
    state.hand.push(peeked);
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    addLog(`${sourceName} — revealed ${peeked.name} and added it to hand.`);
    NET.send({ type: 'CARD_REVEAL', card: peeked, pilotName: sourceName });
    broadcastCardToast(`${peeked.name} was added to hand due to ${sourceName} effect.`);
    broadcastCounters(); render(); setupDropZones();
    finish();
  });
}
// Resolves "look at top card of MY OWN deck; qualifies → may add to hand;
// otherwise/declined → choose top or bottom" — called both locally (by the
// activating player) and from network.js's FORCE_LOOK_TOP_CARD_OR_RETURN
// remote handler (by the opponent, against their own deck).
function resolveLookTopCardOrReturn(sourceName, onDone, qualifies, qualifyLabel) {
  const top = peekTopOfDeck(1)[0];
  if (!top) {
    addLog(`${sourceName} — deck is empty, nothing to look at.`);
    onDone?.();
    return;
  }
  addLog(`${sourceName} — looking at the top card of the deck.`);
  showLookTopCardPeekModal(sourceName, top.card, top.entry, onDone, qualifies, qualifyLabel);
}
const eb01078Qualifies = (peeked) => peeked.type === 'unit';
registerCardEffect('EB01-078', {
  main: (card, ctx) => {
    resolveLookTopCardOrReturn(card.name, () => {
      if (state.networked) NET.send({ type: 'FORCE_LOOK_TOP_CARD_OR_RETURN', cardName: card.name, mode: 'unit' });
      ctx.onDone();
    }, eb01078Qualifies, 'Unit');
  },
});

// ─── EB01-023 · Le Cygne (EX) ──────────────────────────────────────────────
// [Attack] All players each look at the top card of their deck. If it is a
// card that is Lv.5 or higher, they may reveal it and add it to their hand.
// They return any remaining card to the top or bottom of their deck.
// Same "all players independently resolve against their own deck" mechanic
// as EB01-078, just gated on Lv.5+ instead of Unit type, and fired from
// [Attack] instead of [Main].
const eb01023Qualifies = (peeked) => (parseInt(peeked.level) || 0) >= 5;
registerCardEffect('EB01-023', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    resolveLookTopCardOrReturn(card.name, () => {
      if (state.networked) NET.send({ type: 'FORCE_LOOK_TOP_CARD_OR_RETURN', cardName: card.name, mode: 'lv5' });
      ctx.onDone?.();
    }, eb01023Qualifies, 'Lv.5 or higher card');
  },
});

// ─── EB01-080 · Sturm Faust ────────────────────────────────────────────────
// [Main]/[Action] Choose 1 (G Generation) Unit. During this turn, it may
// choose an active enemy Unit as its attack target.
// [Pilot] Jean Luc Duvall
// Uses the CanAttackActiveAll tempKeyword — same convention as GD03-021
// Gundam Deathscythe Hell's deploy effect, just granted via Main/Action here.
function eb01080Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly (G Generation) Units in play. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(
    card.name,
    'Choose 1 (G Generation) Unit. It may choose any active enemy Unit as its attack target this turn.',
    candidates,
    (target) => {
      if (target) {
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveAll')) target.tempKeywords.push('CanAttackActiveAll');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAll' });
        addLog(`${card.name} — ${target.name} can attack any active enemy Unit this turn.`);
        render();
        setupDropZones();
      }
      onDone();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('EB01-080', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(G Generation)')),
  main:   (card, ctx) => eb01080Effect(card, ctx.onDone),
  action: (card, ctx) => eb01080Effect(card, ctx.onDone),
});

// Shared helper: look at top `count` deck cards, let player optionally reveal 1
// matching candidate and add it to hand; return the rest to the bottom randomly.
// `predicate` receives a peeked card and returns true if it qualifies.
// `onDone` is called when the async modal flow finishes (may be null).
function lookAtTopAndPickToHand(sourceCard, count, label, predicate, onDone) {
  const peeked = peekTopOfDeck(count);
  const shuffleToBottom = (entries) => {
    for (let i = entries.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [entries[i], entries[j]] = [entries[j], entries[i]];
    }
    entries.forEach(e => sendDeckEntryToBottom(e));
  };
  if (peeked.length === 0) {
    addLog(`${sourceCard.name} — deck is empty. No effect.`);
    onDone?.();
    return;
  }
  const disabledIndices = peeked
    .map((p, i) => (predicate(p.card) ? -1 : i))
    .filter(i => i !== -1);
  showCardPickerModal(
    sourceCard.name,
    label,
    peeked.map(p => p.card),
    (chosen) => {
      let chosenPeek = null;
      if (chosen) {
        chosenPeek = peeked.find(p => p.card.uid === chosen.uid);
        if (chosenPeek) {
          chosenPeek.entry.state = 'hand';
          state.hand.push(chosenPeek.card);
          state.deckCount = state.tempDeck.filter(isInDeck).length;
          addLog(`${sourceCard.name} — revealed ${chosen.name} and added it to hand.`);
          NET.send({ type: 'CARD_REVEAL', card: chosen, pilotName: sourceCard.name });
          broadcastCardToast(`${chosen.name} was added to hand due to ${sourceCard.name} effect.`);
        }
      }
      const remaining = chosenPeek ? peeked.filter(p => p !== chosenPeek) : peeked;
      shuffleToBottom(remaining.map(p => p.entry));
      broadcastCounters();
      render();
      setupDropZones();
      onDone?.();
    },
    sourceCard,
    { mandatory: false, disabledIndices }
  );
}

// ─── ST06-012 · Schoolgirl and Smuggler ──────────────────────────────────
// [Main] Look at the top 3 cards of your deck. You may reveal 1 (Clan) Unit
// or Pilot card among them and add it to your hand. Return the remaining cards
// randomly to the bottom of your deck.
registerCardEffect('ST06-012', {
  main: (card, ctx) => {
    lookAtTopAndPickToHand(
      card, 3,
      'You may reveal 1 (Clan) Unit or Pilot card and add it to your hand.',
      c => (c.type === 'unit' || c.type === 'pilot' || c.type === 'command pilot') &&
           (c.trait || '').includes('(Clan)'),
      ctx.onDone
    );
  },
});

// ─── ST07-014 · Tactical Visionary ───────────────────────────────────────
// [Main] Look at the top 3 cards of your deck. You may reveal 1 (CB) Unit or
// Pilot card among them and add it to your hand. Return the remaining cards
// randomly to the bottom of your deck.
registerCardEffect('ST07-014', {
  main: (card, ctx) => {
    lookAtTopAndPickToHand(
      card, 3,
      'You may reveal 1 (CB) Unit or Pilot card and add it to your hand.',
      c => (c.type === 'unit' || c.type === 'pilot' || c.type === 'command pilot') &&
           (c.trait || '').includes('(CB)'),
      ctx.onDone
    );
  },
});

// ─── GD02-103 · AGE Device ───────────────────────────────────────────────
// [Burst] Choose 1 (Asuno Family) Pilot card from your trash. Add it to your hand.
// [Main] If you have an (AGE System) Unit in play, place 1 EX Resource.
registerCardEffect('GD02-103', {
  burst: (card) => {
    const eligible = state.playerTrash.filter(c =>
      (c.type === 'pilot' || c.type === 'command pilot') &&
      (c.trait || '').includes('(Asuno Family)')
    );
    if (eligible.length === 0) {
      addLog(`${card.name} — no (Asuno Family) Pilot cards in trash. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 (Asuno Family) Pilot card from your trash. Add it to your hand.',
      eligible,
      (chosen) => {
        if (!chosen) return;
        const idx = state.playerTrash.findIndex(c => c.uid === chosen.uid);
        if (idx !== -1) state.playerTrash.splice(idx, 1);
        state.trashCount = Math.max(0, state.trashCount - 1);
        const dbEntry = cardDb[chosen.cardNo];
        const handCard = dbEntry ? buildGameCardFromDb(dbEntry, chosen.copyVer) : { ...chosen };
        state.hand.push(handCard);
        addLog(`${card.name} — ${chosen.name} retrieved from trash to hand.`);
        broadcastCardToast(`${chosen.name} was added from trash to hand due to ${card.name} effect.`);
        NET.send({ type: 'CARD_REVEAL', card: chosen, pilotName: card.name });
        broadcastCounters();
        render();
      },
      card,
      { mandatory: true }
    );
  },
  main: (card, ctx) => {
    const hasAgeSystem = state.playerField.some(c => c && (c.trait || '').includes('(AGE System)'));
    if (!hasAgeSystem) {
      addLog(`${card.name} — no (AGE System) Unit in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    placeExResource();
    addLog(`${card.name} — placed 1 EX Resource.`);
    ctx.onDone?.();
  },
});

// ─── GD02-104 · Turning Point of History ─────────────────────────────────
// [Main] Look at the top 3 cards of your deck and return 1 to the top. Return
// the remaining cards to the bottom of your deck. Then, if you have a (Newtype)
// Pilot in play, draw 1.
registerCardEffect('GD02-104', {
  main: (card, ctx) => {
    const peeked = peekTopOfDeck(3);
    if (peeked.length === 0) {
      addLog(`${card.name} — deck is empty. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 card to return to the top of your deck. The rest go to the bottom.',
      peeked.map(p => p.card),
      (chosen) => {
        if (!chosen) {
          peeked.forEach(p => sendDeckEntryToBottom(p.entry));
          addLog(`${card.name} — all 3 cards returned to bottom.`);
        } else {
          const chosenPeek  = peeked.find(p => p.card.uid === chosen.uid);
          const rest        = peeked.filter(p => p.card.uid !== chosen.uid);
          rest.forEach(p => sendDeckEntryToBottom(p.entry));
          sendDeckEntryToTop(chosenPeek.entry);
          addLog(`${card.name} — ${chosen.name} returned to top; others sent to bottom.`);
        }
        const hasNewtype = state.playerField.some(c =>
          c && c.pilot && (c.pilot.trait || '').includes('(Newtype)')
        );
        if (hasNewtype) {
          const drawn = drawCardsFromTempDeck(1, true);
          if (drawn.length > 0) {
            state.hand.push(drawn[0]);
            addLog(`${card.name} — (Newtype) Pilot in play: drew ${drawn[0].name}.`);
          }
        }
        broadcastCounters();
        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-105 · Citizens, Take a Stand! ──────────────────────────────────
// [Burst] Add this card to your hand — handled generically (hasBurst check).
// [Main] All your Units get AP+2 during this turn.
registerCardEffect('GD01-105', {
  main: (card, ctx) => {
    const targets = state.playerField.filter(c => c);
    if (targets.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    targets.forEach(c => { c.tempApMod = (c.tempApMod || 0) + 2; });
    NET.send({ type: 'FIELD_AP_MOD', uids: targets.map(c => c.uid), delta: 2, field: 'tempApMod' });
    addLog(`${card.name} — all ${targets.length} friendly Unit(s) get AP+2 this turn.`);
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD04-105 · Encounter ────────────────────────────────────────────────
// [Main] Look at the top 5 cards of your deck. You may reveal 1 Pilot card
// among them and add it to your hand. Return the remaining cards randomly to
// the bottom of your deck.
registerCardEffect('GD04-105', {
  main: (card, ctx) => {
    lookAtTopAndPickToHand(
      card, 5,
      'You may reveal 1 Pilot card and add it to your hand.',
      c => c.type === 'pilot' || c.type === 'command pilot',
      ctx.onDone
    );
  },
});

// ─── GD03-105 · Bridge Crew ──────────────────────────────────────────────
// [Burst] Add this card to your hand — handled generically.
// [Main] Choose 1 friendly Unit. During this turn, it may choose an active
// enemy Unit that has no Pilot paired with it as its attack target.
registerCardEffect('GD03-105', {
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit. It can attack active unpiloted enemy Units this turn.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('CanAttackActiveNoPilot');
        addLog(`${card.name} — ${target.name} can attack active enemy Units with no Pilot this turn.`);
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveNoPilot' });
        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-106 · M.A.V. Tactics ───────────────────────────────────────────
// [Main] Deploy 1 rested [GQuuuuuuX (Omega Psycommu)] token and 1 rested
// [Red Gundam] token.
registerCardEffect('GD03-106', {
  main: (card, ctx) => {
    const t1 = deployTokenToField('T-019'); // GQuuuuuuX (Omega Psycommu), (Clan), AP3/HP2
    if (t1) {
      setRested(t1, true);
      addLog(`${card.name} — deployed ${t1.name} token (rested).`);
    } else {
      addLog(`${card.name} — no empty slot for GQuuuuuuX token.`);
    }
    const t2 = deployTokenToField('T-018'); // Red Gundam, (Clan), AP2/HP3
    if (t2) {
      setRested(t2, true);
      addLog(`${card.name} — deployed ${t2.name} token (rested).`);
    } else {
      addLog(`${card.name} — no empty slot for Red Gundam token.`);
    }
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD01-107 · First Contact ─────────────────────────────────────────────
// [Burst] Place 1 EX Resource.
// [Main] Place 1 rested Resource.
registerCardEffect('GD01-107', {
  burst: (card) => {
    placeExResource();
    addLog(`${card.name} — [Burst] placed 1 EX Resource.`);
  },
  main: (card, ctx) => {
    // A rested resource counts toward level (resBase++) but is not spendable this turn.
    state.resBase = Math.min(state.resBase + 1, 10);
    addLog(`${card.name} — placed 1 rested Resource (level now: ${state.resBase + state.exRes + state.exResRested}).`);
    broadcastCounters();
    render();
    ctx.onDone?.();
  },
});

// ─── GD04-107 · Destined Battle ──────────────────────────────────────────
// [Burst] Add this card to your hand — handled generically.
// [Action] Choose 1 of your rested Units. During this turn, all enemy Units
// must choose that Unit as their attack target when attacking.
registerCardEffect('GD04-107', {
  action: (card, ctx) => {
    const rested = state.playerField.filter(c => c && c.rested);
    if (rested.length === 0) {
      addLog(`${card.name} — no rested friendly Units to target. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 rested friendly Unit. All enemies must attack it this turn.',
      rested,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        state.forcedAttackTargetUid = target.uid;
        NET.send({ type: 'FORCE_ATTACK_TARGET', uid: target.uid });
        addLog(`${card.name} — all enemy Units must attack ${target.name} this turn.`);
        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-108 · Strategic Arms ───────────────────────────────────────────
// [Main] Deal 2 damage to all Units with <Blocker>.
registerCardEffect('GD01-108', {
  main: (card, ctx) => {
    const targets = [
      ...state.playerField.filter(c => c && hasNativeKeyword(c, 'Blocker')),
      ...state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker')),
    ];
    if (targets.length === 0) {
      addLog(`${card.name} — no Units with <Blocker> in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    addLog(`${card.name} — dealing 2 damage to all Units with <Blocker>.`);
    targets.forEach(c => {
      const fromEnemy = state.oppField.includes(c);
      dealDamageToUnit(c, 2, { fromEnemy });
    });
    ctx.onDone?.();
  },
});

// ─── GD01-109 · The Path to Victory or Defeat ────────────────────────────
// [Main] Look at the top 5 cards of your deck. You may reveal 1 (Operation
// Meteor) or (G Team) Unit or Pilot card among them and add it to your hand.
// Return the remaining cards randomly to the bottom of your deck.
registerCardEffect('GD01-109', {
  main: (card, ctx) => {
    lookAtTopAndPickToHand(
      card, 5,
      'You may reveal 1 (Operation Meteor)/(G Team) Unit or Pilot card and add it to your hand.',
      c => {
        const trait = c.trait || '';
        const isUnitOrPilot = c.type === 'unit' || c.type === 'pilot' || c.type === 'command pilot';
        return isUnitOrPilot && (trait.includes('(Operation Meteor)') || trait.includes('(G Team)'));
      },
      ctx.onDone
    );
  },
});

// ─── ST02-012 · Simultaneous Fire ────────────────────────────────────────
// [Main] Choose 1 of your Units. It gains <Breach 3> during this turn.
// [Pilot] [Trowa Barton]
registerCardEffect('ST02-012', {
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your Units. It gains <Breach 3> this turn.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('Breach 3');
        addLog(`${card.name} — ${target.name} gains <Breach 3> this turn.`);
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 3' });
        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST02-013 · Peaceful Timbre ──────────────────────────────────────────
// [Action] During this battle, your shield area cards can't receive damage
// from enemy Units that are Lv.4 or lower.
// [Pilot] [Quatre Raberba Winner]
// Sets state.battleShieldImmuneLv = 4 on both clients (via the action trigger
// firing on both); the defending client checks it in runDamageStep.
registerCardEffect('ST02-013', {
  action: (card, ctx) => {
    state.battleShieldImmuneLv = Math.max(state.battleShieldImmuneLv || 0, 4);
    addLog(`${card.name} — shields are immune to damage from Units Lv.4 or lower this battle.`);
    ctx.onDone?.();
  },
});

// ─── EB01-079 · Modification ──────────────────────────────────────────────
// [Main] Choose 1 friendly (G Generation) Unit. It can't receive battle
// damage from enemy Units that are Lv.3 or lower during this turn.
// Uses BattleImmunityFromLv3 tempKeyword (turn-long, checked in
// runDamageStep — covers Effect damage separately, so it's unaffected).
registerCardEffect('EB01-079', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(G Generation)')),
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (G Generation) Units in play. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      "Choose 1 friendly (G Generation) Unit. It can't receive battle damage from Lv.3 or lower enemy Units this turn.",
      candidates,
      (target) => {
        if (!target) { ctx.onDone(); return; }
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('BattleImmunityFromLv3');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'BattleImmunityFromLv3' });
        addLog(`${card.name} — ${target.name} is immune to battle damage from Lv.3 or lower enemy Units this turn.`);
        render();
        setupDropZones();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST06-013 · Fierce Unity ──────────────────────────────────────────────
// [Action] Choose 1 to 2 friendly (Clan) Units. They can't receive battle
// damage from enemy Units that are Lv.2 or lower during this turn.
// [Pilot] [Ortega (GQ)]
// Uses BattleImmunityFromLv2 tempKeyword (turn-long, checked in runDamageStep).
registerCardEffect('ST06-013', {
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Clan)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Clan) Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModalMulti(
      card.name,
      'Choose 1 to 2 friendly (Clan) Units. They are immune to battle damage from Lv.2 or lower Units this turn.',
      candidates,
      { min: 1, max: 2, mandatory: true },
      (targets) => {
        if (!targets || targets.length === 0) { ctx.onDone?.(); return; }
        targets.forEach(t => {
          t.tempKeywords = t.tempKeywords || [];
          t.tempKeywords.push('BattleImmunityFromLv2');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: t.uid, keyword: 'BattleImmunityFromLv2' });
        });
        addLog(`${card.name} — ${targets.map(t => t.name).join(', ')} immune to battle damage from Lv.2 or lower this turn.`);
        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card
    );
  },
});

// ─── ST03-014 · The Blue Giant ────────────────────────────────────────────
// [Action] Choose 1 friendly Unit. It can't receive battle damage from enemy
// Units with 2 or less AP during this battle.
// [Pilot] [Ramba Ral]
// Uses battleImmuneAp = 2 (per-battle, cleared at start of next runDamageStep).
registerCardEffect('ST03-014', {
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit. It is immune to battle damage from Units with 2 or less AP this battle.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        target.battleImmuneAp = Math.max(target.battleImmuneAp || 0, 2);
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'battleImmuneAp' });
        addLog(`${card.name} — ${target.name} is immune to battle damage from Units with AP 2 or less this battle.`);
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-105 · Valedictorian ─────────────────────────────────────────────
// [Action] Choose 1 of your Unit tokens. It can't receive battle damage from
// enemy Units during this battle.
// [Pilot] [Xavier Olivette]
// Uses battleImmuneAll = true (per-battle, cleared at start of next runDamageStep).
registerCardEffect('GD02-105', {
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && isUnitToken(c));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Unit tokens in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your Unit tokens. It is immune to all battle damage this battle.',
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        target.battleImmuneAll = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'battleImmuneAll' });
        addLog(`${card.name} — ${target.name} is immune to all battle damage this battle.`);
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-106 · Fortress Defense ─────────────────────────────────────────
// [Main] Deploy 2 [Zaku II]((Zeon)·AP1·HP1) Unit tokens.
// [Pilot] [Dozle Zabi]
registerCardEffect('GD01-106', {
  main: (card, ctx) => {
    const t1 = deployTokenToField('T-007');
    if (t1) addLog(`${card.name} — deployed ${t1.name} token.`);
    else addLog(`${card.name} — no empty slot for first Zaku II token.`);
    const t2 = deployTokenToField('T-007');
    if (t2) addLog(`${card.name} — deployed ${t2.name} token.`);
    else if (t1) addLog(`${card.name} — no empty slot for second Zaku II token.`);
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD04-106 · Indiscriminate Violence ──────────────────────────────────
// [Main] Choose 1 friendly (Academy) Unit. During this turn, it may choose an
// active enemy Unit with 5 or less AP as its attack target. If you use an EX
// Resource to play this card, choose 1 to 2 friendly (Academy) Units instead.
// [Pilot] [Norea Du Noc]
registerCardEffect('GD04-106', {
  main: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Academy)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Academy) Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    const applyTargets = (targets) => {
      targets.forEach(t => {
        t.tempKeywords = t.tempKeywords || [];
        t.tempKeywords.push('CanAttackActiveAP5');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: t.uid, keyword: 'CanAttackActiveAP5' });
      });
      addLog(`${card.name} — ${targets.map(t => t.name).join(', ')} can attack active Units with AP 5 or less this turn.`);
      render();
      setupDropZones();
      ctx.onDone?.();
    };
    if (ctx.usedExRes) {
      showCardPickerModalMulti(
        card.name,
        'Choose 1 to 2 friendly (Academy) Units. They can attack active enemy Units with 5 or less AP this turn.',
        candidates,
        { min: 1, max: 2, mandatory: true },
        (targets) => {
          if (!targets || targets.length === 0) { ctx.onDone?.(); return; }
          applyTargets(targets);
        },
        card
      );
    } else {
      showCardPickerModal(
        card.name,
        'Choose 1 friendly (Academy) Unit. It can attack active enemy Units with 5 or less AP this turn.',
        candidates,
        (target) => {
          if (!target) { ctx.onDone?.(); return; }
          applyTargets([target]);
        },
        card,
        { mandatory: true }
      );
    }
  },
});

// ─── GD02-106 · White Wolf ────────────────────────────────────────────────
// [Action] During this battle, your shield area cards can't receive damage
// from enemy Units that are Lv.3 or lower.
// [Pilot] [Woolf Enneacle]
// Sets state.battleShieldImmuneLv = 3 on the defending client.
registerCardEffect('GD02-106', {
  action: (card, ctx) => {
    state.battleShieldImmuneLv = Math.max(state.battleShieldImmuneLv || 0, 3);
    addLog(`${card.name} — shields are immune to damage from Units Lv.3 or lower this battle.`);
    ctx.onDone?.();
  },
});

// ─── GD03-107 · Over the River and Through the Woods ─────────────────────
// [Main] Choose 1 enemy Unit that is Lv.5 or lower. Deal damage to it equal
// to the number of friendly Unit tokens in play.
// [Pilot] [Hardie Steiner]
registerCardEffect('GD03-107', {
  main: (card, ctx) => {
    const tokenCount = state.playerField.filter(c => c && isUnitToken(c)).length;
    if (tokenCount === 0) {
      addLog(`${card.name} — 0 friendly tokens in play; damage would be 0. No effect.`);
      ctx.onDone?.();
      return;
    }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or lower. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      `Choose 1 enemy Unit that is Lv.5 or lower. Deal ${tokenCount} damage to it.`,
      candidates,
      (target) => {
        if (!target) { ctx.onDone?.(); return; }
        addLog(`${card.name} — dealing ${tokenCount} damage to ${target.name}.`);
        dealDamageToUnit(target, tokenCount, { fromEnemy: true });
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-108 · How Many Miles to the Battlefield? ───────────────────────
// [Main] Deploy 1 [Hy-Gogg]((Cyclops Team)·AP2·HP1) Unit token.
// [Pilot] [Gabriel Ramirez Garcia]
// Note: the DB token T-013 is listed with (Zeon) trait; deploy as-is.
registerCardEffect('GD03-108', {
  main: (card, ctx) => {
    const token = deployTokenToField('T-013');
    if (token) {
      addLog(`${card.name} — deployed ${token.name} token.`);
    } else {
      addLog(`${card.name} — no empty field slot for Hy-Gogg token.`);
    }
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD04-108 · Witches from Earth ───────────────────────────────────────
// [Main]/[Action] Choose 1 friendly (Academy) Unit. During this turn, reduce
// the next damage it receives by 2. If you use an EX Resource to play this
// card, reduce by 4 instead.
// [Pilot] [Sophie Pulone]
function gd04108Effect(card, usedExRes, onDone) {
  const reduction = usedExRes ? 4 : 2;
  const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Academy)'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly (Academy) Units in play. No effect.`);
    onDone?.();
    return;
  }
  showCardPickerModal(
    card.name,
    `Choose 1 friendly (Academy) Unit. The next damage it receives is reduced by ${reduction}.`,
    candidates,
    (target) => {
      if (!target) { onDone?.(); return; }
      target.nextDamageReduction = (target.nextDamageReduction || 0) + reduction;
      NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: reduction, field: 'nextDamageReduction' });
      addLog(`${card.name} — ${target.name}: next damage reduced by ${reduction}.`);
      onDone?.();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD04-108', {
  main:   (card, ctx) => gd04108Effect(card, ctx.usedExRes, ctx.onDone),
  action: (card, ctx) => gd04108Effect(card, ctx.usedExRes, ctx.onDone),
});

// ─── GD01-110 · Rascarno's Orders ────────────────────────────────────────
// [Main]/[Action] Choose 1 Unit that is Lv.4 or higher. During this turn, it
// may choose an active enemy Unit with 6 or less AP as its attack target.
// [Pilot] [Rascarno Kurama]
function gd01110Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && (parseInt(c.level) || 0) >= 4);
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly Units Lv.4 or higher. No effect.`);
    onDone?.();
    return;
  }
  showCardPickerModal(
    card.name,
    'Choose 1 Unit that is Lv.4 or higher. It can attack active enemy Units with 6 or less AP this turn.',
    candidates,
    (target) => {
      if (!target) { onDone?.(); return; }
      target.tempKeywords = target.tempKeywords || [];
      target.tempKeywords.push('CanAttackActiveAP6');
      NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP6' });
      addLog(`${card.name} — ${target.name} can attack active Units with AP 6 or less this turn.`);
      render();
      setupDropZones();
      onDone?.();
    },
    card,
    { mandatory: true }
  );
}
registerCardEffect('GD01-110', {
  main:   (card, ctx) => gd01110Effect(card, ctx.onDone),
  action: (card, ctx) => gd01110Effect(card, ctx.onDone),
});

// ════════════════════════════════════════════════════════════════════════════
// GREEN PILOT CARD EFFECTS
// ════════════════════════════════════════════════════════════════════════════

// ─── ST02-010 · Heero Yuy ─────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] This Unit gets AP+1 and HP+1.
// The pilot's `hp: +1` / `ap: +2` DB values are the card's ALWAYS-applied
// base pilot boost (any unit Heero pairs with, Link or not) — the
// "[During Link] AP+1/HP+1" text is a SEPARATE, additional bonus that only
// applies once the unit becomes a genuine Link Unit, stacking on top of the
// base boost. AP+1 was already granted via passiveApBonus; HP+1 was missing
// its passiveHpBonus counterpart entirely, so a linked Unit only ever got
// the base +1 HP instead of +2 total — see GD05-091 Sting Oakley for the
// same paired-hooks convention.
registerCardEffect('ST02-010', {
  passiveApBonus: (pilot, unit) => isLinkUnit(unit) ? 1 : 0,
  passiveHpBonus: (pilot, unit) => isLinkUnit(unit) ? 1 : 0,
});

// ─── ST03-011 · Char Aznable ──────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack] During this turn, this Unit gets AP+1 and, if it is a Link Unit,
// it gains <High-Maneuver>. (This Unit can't be blocked.)
registerCardEffect('ST03-011', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    unit.tempApMod = (unit.tempApMod || 0) + 1;
    NET.send({ type: 'FIELD_AP_MOD', uids: [unit.uid], delta: 1, field: 'tempApMod' });
    addLog(`${card.name} — [Attack] ${unit.name} gets AP+1 this turn.`);
    if (isLinkUnit(unit)) {
      unit.tempKeywords = unit.tempKeywords || [];
      if (!unit.tempKeywords.includes('High-Maneuver')) unit.tempKeywords.push('High-Maneuver');
      NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'High-Maneuver' });
      addLog(`${card.name} — ${unit.name} (Link Unit) gains <High-Maneuver> this turn.`);
    }
    render();
    setupDropZones();
    ctx?.onDone?.();
  },
});

// ─── EB01-067 · Asuna Elmarit — Pilot ─────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Look at the top 3 cards of your deck. You may reveal 1
// (G Generation) Unit card among them and return it to the top of your
// deck. Return the remaining cards randomly to the bottom of your deck.
// Fires on plain Pair OR Link (no [During Link] qualifier). Same
// peek/shuffle-to-bottom machinery as lookAtTopAndPickToHand, but the
// chosen card returns to the TOP of the deck instead of the hand (same
// convention as GD02-104 Turning Point of History).
registerCardEffect('EB01-067', {
  pair: (card) => {
    const peeked = peekTopOfDeck(3);
    if (peeked.length === 0) {
      addLog(`${card.name} — deck is empty. No effect.`);
      return;
    }
    const shuffleToBottom = (entries) => {
      for (let i = entries.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [entries[i], entries[j]] = [entries[j], entries[i]];
      }
      entries.forEach(e => sendDeckEntryToBottom(e));
    };
    const isQualifying = p => p.card.type === 'unit' && (p.card.trait || '').includes('(G Generation)');
    // Show all 3 peeked cards (not just the eligible ones) with ineligible
    // ones greyed out via disabledIndices, so the player can see everything
    // that was looked at — same fix as GD02-094 Garrod Ran & Tiffa Adill.
    const disabledIndices = peeked.map((p, i) => i).filter(i => !isQualifying(peeked[i]));
    showCardPickerModal(
      card.name,
      'Look at the top 3 cards of your deck. You may reveal 1 (G Generation) Unit card and return it to the top of your deck.',
      peeked.map(p => p.card),
      (chosen) => {
        let chosenPeek = null;
        if (chosen) {
          chosenPeek = peeked.find(p => p.card.uid === chosen.uid);
          sendDeckEntryToTop(chosenPeek.entry);
          addLog(`${card.name} — revealed ${chosen.name} and returned it to the top of the deck.`);
          NET.send({ type: 'CARD_REVEAL', card: chosen, pilotName: card.name });
        }
        const remaining = chosenPeek ? peeked.filter(p => p !== chosenPeek) : peeked;
        shuffleToBottom(remaining.map(p => p.entry));
        broadcastCounters();
        render();
        setupDropZones();
      },
      card,
      { mandatory: false, disabledIndices }
    );
  },
});

// ─── EB01-068 · Chall Acustica — Pilot ────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Destroyed] You may return the card paired with this Unit
// (this Pilot) to the top of its owner's deck.
//
// Fires from two distinct destruction paths in game.js, handled differently:
//  - Battle destruction (runDamageStep): ctx.rescuePilot is available (now
//    threaded to the pilot's own 'destroyed' ctx alongside the owning
//    Unit's, see the rescueDestroyedPilot/rescueAttackerPilot call sites) to
//    pull the pilot's pending trash-card out before runEpilogue commits it.
//  - Effect-damage destruction (dealDamageToUnit's doDestroy): the pilot is
//    already pushed straight into state.playerTrash by the time 'destroyed'
//    fires (no pending-list rescue exists there), so it's spliced back out
//    of the real trash array instead.
// Either way, the card is then relocated into the deck via updateTempState +
// sendDeckEntryToTop (same mechanism as ST09-001 Impulse Gundam's self-return
// to the deck).
registerCardEffect('EB01-068', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const unit = ctx.unit;
    if (!unit || !isLinkUnit(unit)) return; // [During Link] only
    if (ctx.ownerSide && ctx.ownerSide !== 'player') return;

    showCardPickerModal(
      card.name,
      'You may return this card to the top of your deck.',
      [card],
      (chosen) => {
        if (!chosen) return;
        if (ctx.rescuePilot) {
          ctx.rescuePilot();
        } else {
          const idx = state.playerTrash.findIndex(c => c.uid === card.uid);
          if (idx !== -1) {
            state.playerTrash.splice(idx, 1);
            state.trashCount = Math.max(0, state.trashCount - 1);
          }
        }
        updateTempState(card.cardNo, card.copyVer, 'deck');
        const entry = state.tempDeck.find(e => e.cardNo === card.cardNo && e.copyVer === card.copyVer);
        if (entry) sendDeckEntryToTop(entry);
        else state.tempDeck.unshift({ cardNo: card.cardNo, copyVer: card.copyVer, state: 'deck' });
        addLog(`${card.name} — returned to the top of your deck.`);
        broadcastCounters();
        render();
      },
      card,
      { skipLabel: 'No' }
    );
  },
});

// ─── GD01-090 · Duo Maxwell ───────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] This Unit's AP can't be reduced by enemy effects.
// Implemented via passiveApReduceImmune hook — checked in applyApMod (white.js)
// and NET.onFieldApMod (game.js) before applying negative tempApMod/battleApMod.
registerCardEffect('GD01-090', {
  passiveApReduceImmune: (pilot, unit) => isLinkUnit(unit),
});

// ─── GD01-091 · Chang Wufei ───────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// During your turn, if this Unit has <Breach>, it can't receive battle damage
// from enemy Units with 3 or less AP.
// passiveAttackerImmuneFromDefender: checked in runDamageStep before applying
// counter-damage to the attacker (only relevant when it's the player's turn).
registerCardEffect('GD01-091', {
  passiveAttackerImmuneFromDefender: (pilot, unit, defender, attackerSide) => {
    if (attackerSide !== 'player' || !state.myTurn) return false;
    if (getBreachValue(unit) <= 0) return false;
    return effectiveBp(defender) <= 3;
  },
});

// ─── GD01-092 · M'Quve ────────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is (Zeon), it gains <Breach 1>.
// No registration needed: M'Quve's effect text contains "Breach 1" which
// getBreachValue() automatically detects from card.pilot.effect when paired.
// (The (Zeon) condition is always true for units M'Quve can be paired with.)

// ─── ST06-010 · Shuji Itō ─────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Attack] If you have a (Clan) Unit in play, look at the top
// card of your deck. Return it to the top or bottom of your deck.
registerCardEffect('ST06-010', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx?.onDone?.(); return; }
    const hasClanUnit = state.playerField.some(c => c && (c.trait || '').includes('(Clan)'));
    if (!hasClanUnit) { ctx?.onDone?.(); return; }
    const topPeek = peekTopOfDeck(1)[0];
    if (!topPeek) {
      addLog(`${card.name} — no cards in deck.`);
      ctx?.onDone?.(); return;
    }
    const { entry: topEntry, card: topCard } = topPeek;
    addLog(`${card.name} — [During Link][Attack] looking at top card: ${topCard.name}.`);
    // Same card-image + stats presentation as showTopDeckPeekPrompt (used by
    // e.g. ST09-010 Minerva's [Deploy] peek) instead of the old bespoke
    // text-only overlay, which never showed the card's image. Built inline
    // rather than reusing showTopDeckPeekPrompt directly since that helper's
    // buttons are hardcoded to "Add to Hand" / "Return to Bottom" — Shuji's
    // choice is "Return to Top" / "Return to Bottom", not a hand pickup.
    const overlay = document.createElement('div');
    overlay.id = 'ingame-menu-overlay';

    const row = (label, val) =>
      `<div class="cp-row">
         <span class="cp-label">${label}</span>
         <span class="cp-val">${val ?? '—'}</span>
       </div>`;

    const modal = document.createElement('div');
    modal.id = 'ingame-menu-modal';
    modal.innerHTML = `
      <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
      <div class="igm-title">Top of Deck</div>
      <div class="srb-content">
        <div class="cp-img-wrap">
          ${topCard.img ? `<img src="${topCard.img}" alt="${topCard.name}" />` : '<div class="cp-no-img">?</div>'}
        </div>
        <div class="cp-stats">
          <div class="cp-name">${topCard.name}</div>
          ${topCard.trait ? `<div class="cp-trait">${topCard.trait}</div>` : ''}
          <div class="cp-divider"></div>
          <div class="cp-stat-grid">
            ${row('Level', topCard.level)}
            ${row('Cost',  topCard.cost)}
            ${row('AP',    topCard.bp)}
            ${row('HP',    topCard.hp)}
            ${row('Type',  displayCardType(topCard.type))}
          </div>
          <div class="cp-effect">${topCard.effect ?? '—'}</div>
        </div>
      </div>
      <div class="igm-body">${card.name} — return ${topCard.name} to the top or bottom of your deck.</div>
      <div class="igm-btn-row">
        <button class="igm-btn igm-return-bottom">Return to Bottom</button>
        <button class="igm-btn igm-confirm-base">Return to Top</button>
      </div>
    `;
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
    modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      hideModalForFieldView(overlay);
    });
    modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
      overlay.remove();
      addLog(`${card.name} — ${topCard.name} returned to top of deck.`);
      ctx?.onDone?.();
    });
    // Deliberately NOT .igm-cancel — autoResolveIgmModal's generic timeout
    // handler treats .igm-cancel as a safe "decline/skip" default, but this
    // is a meaningful alternate choice (not a decline), so it must fall
    // through to the "Return to Top" (.igm-confirm-base) button instead —
    // see autoResolveIgmModal's own comment for the full rationale.
    modal.querySelector('.igm-return-bottom').addEventListener('click', () => {
      overlay.remove();
      sendDeckEntryToBottom(topEntry);
      addLog(`${card.name} — ${topCard.name} returned to bottom of deck.`);
      ctx?.onDone?.();
    });
  },
});

// ─── GD02-088 · Flit Asuno ────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] Look at the top 3 cards of your deck. You may reveal 1 green
// (Earth Federation) Unit card / 1 card with "AGE Device" in its card name
// among them and add it to your hand. Return the remaining cards randomly to
// the bottom of your deck.
registerCardEffect('GD02-088', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    lookAtTopAndPickToHand(
      card,
      3,
      'Look at the top 3 cards of your deck. Reveal 1 green (Earth Federation) Unit or card with "AGE Device" in its name to add to your hand.',
      c => {
        const isGreenEFUnit = (c.color || '').toLowerCase() === 'green' &&
                              (c.trait || '').includes('(Earth Federation)') &&
                              (c.type === 'unit');
        const hasAgeDevice  = (c.name || '').includes('AGE Device');
        return isGreenEFUnit || hasAgeDevice;
      }
    );
  },
});

// ─── EB01-065 · Meir Siva — Pilot ─────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Linked] Choose 1 friendly (G Generation) Unit. It gains <Breach 1>
// during this turn.
// Uses pair trigger + isLinkUnit guard — only fires when the link
// requirement is actually satisfied, not on a plain Pair.
registerCardEffect('EB01-065', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx?.unit)) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (G Generation) Units in play. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly (G Generation) Unit. It gains <Breach 1> during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Breach 1')) target.tempKeywords.push('Breach 1');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 1' });
        addLog(`${card.name} — ${target.name} gains <Breach 1> this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-089 · Lalah Sune ────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 of your other (Zeon) Link Units. It gains <Breach 1>
// during this turn.
registerCardEffect('GD02-089', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit) return;
    const candidates = state.playerField.filter(c =>
      c && c.uid !== unit.uid && isLinkUnit(c) && (c.trait || '').includes('(Zeon)')
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no other (Zeon) Link Units in play.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your other (Zeon) Link Units. It gains <Breach 1> during this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Breach 1')) target.tempKeywords.push('Breach 1');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 1' });
        addLog(`${card.name} — ${target.name} gains <Breach 1> this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-090 · Challia Bull (GQ) ─────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While you have another Unit with <High-Maneuver> in play, this Unit gets AP+1.
// Evaluated live so the bonus appears/disappears as the field state changes.
registerCardEffect('GD02-090', {
  passiveApBonus: (pilot, unit) => {
    const ownerSide = state.playerField.some(c => c && c.uid === unit.uid) ? 'player' : 'opp';
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const hasHMUnit = field.some(c => c && c.uid !== unit.uid && hasNativeKeyword(c, 'High-Maneuver'));
    return hasHMUnit ? 1 : 0;
  },
});

// ─── GD03-088 · Asemu Asuno ───────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] If this is an (AGE System) Unit, it gets AP+1 and <Breach 1>.
// AP+1 via passiveApBonus; Breach 1 via pilotConditionalBreachValue (both
// gated on the unit having (AGE System) trait and being a Link Unit).
registerCardEffect('GD03-088', {
  passiveApBonus: (pilot, unit) =>
    (isLinkUnit(unit) && (unit.trait || '').includes('(AGE System)')) ? 1 : 0,
  pilotConditionalBreachValue: (pilot, unit) =>
    (isLinkUnit(unit) && (unit.trait || '').includes('(AGE System)')) ? 1 : 0,
});

// ─── GD03-089 · Bernard Wiseman ───────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// Increase this Unit's AP by an amount equal to the number of (Cyclops Team)
// Pilot cards / Command cards with unique names in your trash.
registerCardEffect('GD03-089', {
  passiveApBonus: (pilot, unit) => {
    const ownerSide = state.playerField.some(c => c && c.uid === unit.uid) ? 'player' : 'opp';
    const trash = state[`${ownerSide}Trash`] || [];
    const uniqueCardNos = new Set(
      trash
        .filter(c => {
          if ((c.type === 'pilot') && (c.trait || '').includes('(Cyclops Team)')) return true;
          if (c.type === 'command' || c.type === 'command pilot') return true;
          return false;
        })
        .map(c => c.cardNo)
    );
    return uniqueCardNos.size;
  },
});

// ─── GD03-090 · Mikhail Kaminsky ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Attack] Choose 1 of your (Cyclops Team) Units. It gains <Breach 1> during
// this turn.
registerCardEffect('GD03-090', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Cyclops Team)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Cyclops Team) Units in play.`);
      ctx?.onDone?.(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Cyclops Team) Units. It gains <Breach 1> during this turn.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Breach 1')) target.tempKeywords.push('Breach 1');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 1' });
        addLog(`${card.name} — ${target.name} gains <Breach 1> this turn.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST07-011 · Lockon Stratos (Neil) ─────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] If this is a (CB) Unit, it may choose an active enemy Unit
// whose Lv. is equal to or lower than this Unit as its attack target this turn.
// Grants CanAttackActiveLv<N> via pilotKeywords (persists while linked).
// hasNativeKeyword checks pilotKeywords, and setupDropZones uses hasNativeKeyword.
registerCardEffect('ST07-011', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !(unit.trait || '').includes('(CB)')) return;
    const unitLevel = parseInt(unit.level) || 0;
    if (unitLevel <= 0) return;
    unit.pilotKeywords = unit.pilotKeywords || [];
    const kw = `CanAttackActiveLv${unitLevel}`;
    if (!unit.pilotKeywords.includes(kw)) unit.pilotKeywords.push(kw);
    addLog(`${card.name} — ${unit.name} (CB, Lv.${unitLevel}) can now attack active enemy Units of Lv.${unitLevel} or lower.`);
    render();
    setupDropZones();
  },
});

// ─── EB01-066 · Reiji — Pilot ─────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 friendly (G Generation) Unit. During this turn, it
// may choose an active enemy Unit with <Blocker> as its attack target.
// Uses the CanAttackActiveBlocker tempKeyword (see showAttackTargetPicker
// in game.js), same convention as ST06-007's CanAttackActiveAP3 grant.
// Fires on plain Pair OR Link (no [During Link] qualifier).
registerCardEffect('EB01-066', {
  pair: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (G Generation) Units in play. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly (G Generation) Unit. It may choose an active enemy Unit with <Blocker> as its attack target this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveBlocker')) target.tempKeywords.push('CanAttackActiveBlocker');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveBlocker' });
        addLog(`${card.name} — ${target.name} may attack active enemy Units with <Blocker> this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST14-012 · Banagher Links — Pilot ────────────────────────────────────
// [When Paired] Choose 1 friendly Unit that is Lv.5 or higher. During this
// turn, it may choose an active enemy Unit with 5 or less AP as its attack
// target. Same CanAttackActiveAP<N> convention as GD02-123 Sodon/ST06-007.
// Fires on plain Pair (no [During Link] qualifier).
registerCardEffect('ST14-012', {
  pair: (card) => {
    const candidates = state.playerField.filter(c => c && (parseInt(c.level) || 0) >= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units Lv.5 or higher in play. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit that is Lv.5 or higher. It may choose an active enemy Unit with 5 or less AP as its attack target this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveAP5')) target.tempKeywords.push('CanAttackActiveAP5');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP5' });
        addLog(`${card.name} — ${target.name} may attack active enemy Units with 5 or less AP this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST07-012 · Allelujah Haptism ─────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// During your turn, while you have a (CB) Link Unit in play, this Unit can't
// receive battle damage from enemy Units with 3 or less AP.
// Own unit counts toward the (CB) Link Unit condition (per user ruling).
registerCardEffect('ST07-012', {
  passiveAttackerImmuneFromDefender: (pilot, unit, defender, attackerSide) => {
    if (attackerSide !== 'player' || !state.myTurn) return false;
    const hasCBLinkUnit = state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(CB)'));
    if (!hasCBLinkUnit) return false;
    return effectiveBp(defender) <= 3;
  },
});

// ─── GD04-085 · Suletta Mercury ───────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Once per Turn] When you play and activate an (Academy) Command
// card using an EX Resource, if you have no remaining EX Resources, place 1
// rested EX Resource.
// Fires via firePilotCommandPassives() in showCommandActivateModal / resolveActionPlay.
registerCardEffect('GD04-085', {
  onAcademyCommandWithExUsed: (pilot, unit, commandCard, onDone) => {
    if (pilot.pilotEffectUsedThisTurn) { onDone(); return; }
    pilot.pilotEffectUsedThisTurn = true;
    placeExResource(true);
    addLog(`${pilot.name} — [During Link][Once per Turn] (Academy) Command played with EX, none remain: place 1 rested EX Resource.`);
    onDone();
  },
});

// ─── GD04-086 · Garma Zabi ────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Destroyed] If you have no EX Resources, place 1 EX Resource.
// "No EX Resources" = active EX only (exRes === 0) per user ruling.
registerCardEffect('GD04-086', {
  destroyed: (card, ctx) => {
    if (!ctx?.unit || !isLinkUnit(ctx.unit)) return;
    if (ctx.ownerSide !== 'player') return;
    if (state.exRes !== 0) return;
    placeExResource(false);
    addLog(`${card.name} — ${ctx.unit.name} destroyed while linked with no EX Resources: placed 1 EX Resource.`);
  },
});

// ─── GD04-087 · Elan Ceres (Enhanced Person Number 5) ────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link][Attack] You may choose 1 of your (Academy) Units. During this
// battle, battle damage this Unit would receive is dealt to that Unit instead.
// Sets state.attackerBdProxyUid (captured in runDamageStep before damage calc).
registerCardEffect('GD04-087', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx?.onDone?.(); return; }
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Academy)'));
    if (candidates.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModal(
      card.name,
      'You may choose 1 friendly (Academy) Unit. Counter-damage this Unit receives this battle is dealt to that Unit instead.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        state.attackerBdProxyUid = target.uid;
        NET.send({ type: 'SET_ATTACKER_PROXY', uid: target.uid });
        addLog(`${card.name} — counter-damage to ${ctx.attacker.name} will be redirected to ${target.name} this battle.`);
        ctx?.onDone?.();
      },
      card,
      { mandatory: false }
    );
  },
});

// ─── GD04-088 · Tokwan ────────────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// When this Unit is blocked by an enemy Unit that is Lv.4 or lower, it can't
// receive battle damage during this battle.
// "Blocked by" = when attacking and the defender is Lv.4 or lower.
registerCardEffect('GD04-088', {
  passiveAttackerImmuneFromDefender: (pilot, unit, defender, attackerSide) => {
    if (attackerSide !== 'player') return false;
    return (parseInt(defender.level) || 0) <= 4;
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GREEN UNIT CARD EFFECTS
// ════════════════════════════════════════════════════════════════════════════

// ─── Development <N> keyword (EB01/ST10 cards) ────────────────────────────
// "[Trigger･Development N] You may exile N (G Generation) Unit cards from
// your trash. If you do, activate the following effect: ■ ..."
// Most Development cards restrict the exile pool to Unit cards ("(G
// Generation) Units"/"Unit cards"), but a few say "(G Generation) cards"
// with no Unit qualifier (e.g. EB01-060 Gundam Aquarius) — pass
// { unitOnly: false } for those.
// Shared helper: candidates are cards with the (G Generation) trait in the
// player's own trash (optionally restricted to Unit type). If fewer than N
// qualify, the option isn't offered at all (no effect). Otherwise shows a
// skippable exact-N picker (showCardPickerModalMulti with mandatory:false,
// min===max===count); on confirm, exiles the chosen cards (mirrored via
// TRASH_EXILE, same convention as purple.js's exileFromTrash) then runs the
// bullet effect via onProceed().
function resolveDevelopmentCost(card, count, onProceed, { unitOnly = true } = {}) {
  const candidates = state.playerTrash.filter(c =>
    (!unitOnly || c.type === 'unit') && (c.trait || '').includes('(G Generation)')
  );
  const label = unitOnly ? 'Unit card' : 'card';
  if (candidates.length < count) {
    addLog(`${card.name} — fewer than ${count} (G Generation) ${label}${count !== 1 ? 's' : ''} in trash. No effect.`);
    return;
  }
  showCardPickerModalMulti(
    card.name,
    `You may exile ${count} (G Generation) ${label}${count !== 1 ? 's' : ''} from your trash. If you do, activate the following effect.`,
    candidates,
    { min: count, max: count, mandatory: false },
    (chosen) => {
      if (!chosen || chosen.length < count) return;
      exileCardsFromTrash(chosen);
      addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
      broadcastCounters();
      render();
      setupDropZones();
      onProceed();
    },
    card
  );
}

// ─── EB01-027 · Tallgeese ──────────────────────────────────────────────────
// [Deploy･Development 2] You may exile 2 (G Generation) Unit cards from your
// trash. If you do: Choose 1 friendly (G Generation) Unit. It gains
// <Breach 1> during this turn.
registerCardEffect('EB01-027', {
  deploy: (card) => {
    resolveDevelopmentCost(card, 2, () => {
      const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(G Generation)'));
      if (candidates.length === 0) {
        addLog(`${card.name} — no friendly (G Generation) Units in play. No effect.`);
        return;
      }
      showCardPickerModal(
        card.name,
        'Choose 1 friendly (G Generation) Unit. It gains <Breach 1> during this turn.',
        candidates,
        (target) => {
          if (!target) return;
          target.tempKeywords = target.tempKeywords || [];
          if (!target.tempKeywords.includes('Breach 1')) target.tempKeywords.push('Breach 1');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 1' });
          addLog(`${card.name} — ${target.name} gains <Breach 1> this turn.`);
          render();
          setupDropZones();
        },
        card,
        { mandatory: true }
      );
    });
  },
});

// ─── EB01-037 · Zudah Unit 1 ───────────────────────────────────────────────
// During your turn, while this Unit is battling an enemy Unit with
// <Blocker>, this Unit can't receive battle damage.
// Attacks only ever happen on the attacking player's own turn, so "during
// your turn" means this Unit can only ever be the attacker (never the
// defender) — passiveUnitAttackerImmuneFromDefender alone covers it, same
// convention as GD02-006 Forbidden Gundam's "during your turn, Lv.2 or
// lower" immunity. Effect damage is unaffected (checked separately in
// dealDamageToUnit, not runDamageStep).
registerCardEffect('EB01-037', {
  passiveUnitAttackerImmuneFromDefender: (unit, defender, attackerSide) =>
    state.myTurn && attackerSide === 'player' && hasNativeKeyword(defender, 'Blocker'),
});

// ─── EB01-028 · Gundam Plutone ─────────────────────────────────────────────
// [Once per Turn] When another Unit attacks an enemy Unit, if this Unit is
// rested, the attacking Unit gains <Breach 2> during this battle.
// (Card text doesn't say "friendly" — restricted to friendly Units per the
// user's explicit clarification, same convention as GD01-121.)
// Fires via the onFriendlyUnitAttacks hook (fireAttackerUnitAttackTrigger in
// game.js), which only notifies OTHER units on the attacker's own side.
registerCardEffect('EB01-028', {
  onFriendlyUnitAttacks: (card, attacker, ctx) => {
    if (card.passiveUsedThisTurn) return;
    if (!card.rested) return;
    if (!ctx || ctx.isShield || ctx.isDirect) return;
    const defender = ctx.defender;
    if (!defender || (defender.type !== 'unit' && defender.type !== 'unit-token')) return;
    card.passiveUsedThisTurn = true;
    attacker.tempKeywords = attacker.tempKeywords || [];
    if (!attacker.tempKeywords.includes('Breach 2')) attacker.tempKeywords.push('Breach 2');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: attacker.uid, keyword: 'Breach 2' });
    addLog(`${card.name} — rested, ${attacker.name} attacks an enemy Unit: gains <Breach 2> this battle.`);
    render();
  },
});

// ─── EB01-036 · Darilbalde ─────────────────────────────────────────────────
// During your turn, all other (G Generation) Units that are Lv.3 get AP+1.
// globalUnitApBonus is checked live via getGlobalUnitApBonus() in
// effectiveBp() for every unit on the field.
// "During your turn" must be read relative to ownerSide, not state.myTurn
// directly — see ST01-001 Gundam (blue.js) for the full rationale: the old
// `ownerSide !== 'player'` short-circuit made this always return 0 when
// evaluated from the non-controlling client's mirrored perspective, causing
// an actual HP desync in networked play (that client computed 1 less AP for
// the boosted target than the controller's own client did).
registerCardEffect('EB01-036', {
  globalUnitApBonus: (targetCard, sourceCard, ownerSide) => {
    const isControllersTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    if (!isControllersTurn) return 0;
    if (targetCard.uid === sourceCard.uid) return 0;
    if ((parseInt(targetCard.level) || 0) !== 3) return 0;
    if (!(targetCard.trait || '').includes('(G Generation)')) return 0;
    return 1;
  },
});

// ─── EB01-035 · Gundam Lfrith Thorn ─────────────────────────────────────────
// When another friendly (G Generation) Unit that is Lv.3 is deployed, this
// Unit gets <Breach 1> during this turn.
// (Card text says THIS Unit gains Breach — not the newly-deployed one —
// despite the effect reading like a deploy-trigger buff on the new arrival.)
// onAnyPlayerUnitDeployed fires on every other field unit when a new Unit
// is played (see PLAY_CARD in game.js).
registerCardEffect('EB01-035', {
  onAnyPlayerUnitDeployed: (card, deployed) => {
    if (!(deployed.trait || '').includes('(G Generation)')) return;
    if ((parseInt(deployed.level) || 0) !== 3) return;
    card.tempKeywords = card.tempKeywords || [];
    if (!card.tempKeywords.includes('Breach 1')) card.tempKeywords.push('Breach 1');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'Breach 1' });
    addLog(`${card.name} — ${deployed.name} (G Generation, Lv.3) deployed: gains <Breach 1> this turn.`);
    render();
  },
});

// ─── EB01-031 · Oggo ────────────────────────────────────────────────────────
// This Unit may choose an active enemy Unit that is Lv.3 or lower as its
// attack target.
// Always-on passive — granted via passiveKeywords so hasNativeKeyword()
// picks it up in showAttackTargetPicker's CanAttackActiveLv<N> check.
registerCardEffect('EB01-031', {
  passiveKeywords: () => ['CanAttackActiveLv3'],
});

// ─── EB01-033 · Taurus (Sanc Kingdom) ───────────────────────────────────────
// [Activate･Action][Once per Turn]①: Choose 1 other Unit that is being
// attacked. It gets AP+1 during this battle.
// RES cost isn't paid generically for a Unit's own Activate·Action (only
// Base Activate·Main is), so it's checked/spent by hand — same convention
// as GD01-058/GD01-082. Target is restricted to the OTHER friendly Unit
// actually being attacked by the enemy this Action Step.
function eb01033Target(card, battle) {
  if (!battle || battle.attackerSide !== 'opp' || battle.defenderSide !== 'player') return null;
  const target = battle.defender;
  if (!target || target.uid === card.uid || target.isShield || target.isDirect) return null;
  if (target.type !== 'unit' && target.type !== 'unit-token') return null;
  return target;
}
registerCardEffect('EB01-033', {
  canAction: (card, ctx) => {
    if ((state.res + state.exRes) < 1) return false;
    return !!eb01033Target(card, ctx);
  },
  action: (card, ctx) => {
    const battle = ctx.battle || ctx;
    const target = eb01033Target(card, battle);
    if (!target) {
      addLog(`${card.name} — no other friendly Unit being attacked. No effect.`);
      ctx.onDone(); return;
    }
    if ((state.res + state.exRes) < 1) {
      addLog(`${card.name} — need ① RES to activate (have ${state.res + state.exRes}).`);
      ctx.onDone(); return;
    }
    spendResources(1);
    broadcastCounters();
    applyApMod([target], 1, 'battleApMod');
    addLog(`${card.name} — ${target.name} gets AP+1 during this battle.`);
    render();
    ctx.onDone();
  },
});

// ─── EB01-021 · Build Strike Gundam (Full Package) (EX) ─────────────────────
// <Breach 4> (native).
// [When Paired･(G Generation) Pilot] If there are 2 or more (G Generation)
// Unit cards in your trash, place 1 rested Resource.
// "Place 1 rested Resource" raises resBase (counts toward level) without
// making it spendable this turn — same convention as GD01-107 First Contact.
registerCardEffect('EB01-021', {
  pair: (card) => {
    if (!card.pilot || !(card.pilot.trait || '').includes('(G Generation)')) return;
    const ggenTrashCount = state.playerTrash.filter(c =>
      c.type === 'unit' && (c.trait || '').includes('(G Generation)')
    ).length;
    if (ggenTrashCount < 2) {
      addLog(`${card.name} — fewer than 2 (G Generation) Unit cards in trash. No effect.`);
      return;
    }
    state.resBase = Math.min(state.resBase + 1, 10);
    addLog(`${card.name} — paired with a (G Generation) Pilot; 2+ (G Generation) Units in trash: placed 1 rested Resource (level now: ${state.resBase + state.exRes + state.exResRested}).`);
    broadcastCounters();
    render();
  },
});

// ─── EB01-022 · Gundam Exia (EX) ───────────────────────────────────────────
// <Breach 5> (native).
// [During Pair･(G Generation) Pilot] At the end of your turn, you may
// destroy this Unit. If you do, deploy 3 [Gundam Exia] ((G Generation)·AP2·
// HP2) Unit tokens.
// Uses the existing T-025 [Gundam Exia] token card (ap:2, hp:2, trait
// "(G Generation)") via deployTokenToField.
registerCardEffect('EB01-022', {
  endOfTurn: (card, onDone) => {
    if (!card.pilot || !(card.pilot.trait || '').includes('(G Generation)')) { onDone(); return; }
    showCardPickerModal(
      card.name,
      'You may destroy this Unit. If you do, deploy 3 [Gundam Exia] Unit tokens.',
      [card],
      (chosen) => {
        if (!chosen) { onDone(); return; }
        const slot = state.playerField.findIndex(c => c && c.uid === card.uid);
        if (slot !== -1) dispatch('DISCARD_CARD', { uid: card.uid, source: 'field', slot });
        addLog(`${card.name} — destroyed itself.`);
        let deployedCount = 0;
        for (let i = 0; i < 3; i++) {
          if (deployTokenToField('T-025')) deployedCount++;
        }
        addLog(`${card.name} — deployed ${deployedCount} [Gundam Exia] token(s).`);
        broadcastCounters();
        render();
        setupDropZones();
        onDone();
      },
      card,
      { skipLabel: 'No' }
    );
  },
});

// ─── EB01-024 · GQuuuuuuX (Omega Psycommu) ──────────────────────────────────
// <Breach 3> (native).
// [Attack] Choose 1 enemy Unit with <Blocker> that is Lv.5 or lower. Deal 2
// damage to it.
registerCardEffect('EB01-024', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c =>
      c && hasNativeKeyword(c, 'Blocker') && (parseInt(c.level) || 0) <= 5
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with <Blocker> Lv.5 or lower. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with <Blocker> that is Lv.5 or lower. Deal 2 damage to it.', candidates, (target) => {
      if (target) {
        dealDamageToUnit(target, 2, { fromEnemy: true });
        addLog(`${card.name} — dealt 2 damage to ${target.name}.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── EB01-025 · Tallgeese II ───────────────────────────────────────────────
// [Deploy･Development 2] You may exile 2 (G Generation) Unit cards from your
// trash. If you do: All players place 1 EX Resource.
// [During Pair] While your opponent has an EX Resource, this Unit can't
// receive battle damage from enemy Units that are Lv.5 or lower (effect
// damage is unaffected).
// "All players place 1 EX Resource" mirrors to the opponent's own deck/state
// via FORCE_PLACE_EX_RESOURCE (network.js), same "each side handles their
// own state" convention as FORCE_DRAW.
registerCardEffect('EB01-025', {
  deploy: (card) => {
    resolveDevelopmentCost(card, 2, () => {
      placeExResource();
      addLog(`${card.name} — placed 1 EX Resource.`);
      if (state.networked) NET.send({ type: 'FORCE_PLACE_EX_RESOURCE' });
    });
  },
  passiveUnitBattleDamageImmune: (card, attacker) => {
    if (!card.pilot) return false;
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const opponentExResTotal = ownerSide === 'player'
      ? (state.oppExRes || 0) + (state.oppExResRested || 0)
      : (state.exRes || 0) + (state.exResRested || 0);
    if (opponentExResTotal < 1) return false;
    return (parseInt(attacker.level) || 0) <= 5;
  },
});

// ─── EB01-030 · Big-Rang ─────────────────────────────────────────────────────
// [Deploy] Look at the top 3 cards of your deck. You may reveal 1
// (G Generation) Unit card that is Lv.3 among them and add it to your hand.
// Return the remaining cards randomly to the bottom of your deck.
// Identical qualifying condition to EB01-034 Gundam Lfrith Ur, just fired
// from [Deploy] instead of [When Linked].
registerCardEffect('EB01-030', {
  deploy: (card) => {
    lookAtTopAndPickToHand(
      card,
      3,
      'Look at the top 3 cards of your deck. Reveal 1 (G Generation) Unit card that is Lv.3 to add to your hand.',
      (peeked) => peeked.type === 'unit' && (peeked.trait || '').includes('(G Generation)') && (parseInt(peeked.level) || 0) === 3,
      null
    );
  },
});

// ─── EB01-034 · Gundam Lfrith Ur ─────────────────────────────────────────────
// [When Linked] Look at the top 3 cards of your deck. You may reveal 1
// (G Generation) Unit card that is Lv.3 among them and add it to your hand.
// Return the remaining cards randomly to the bottom of your deck.
// Uses pair trigger + isLinkUnit guard, and the shared
// lookAtTopAndPickToHand helper (peek/shuffle-to-bottom machinery).
registerCardEffect('EB01-034', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    lookAtTopAndPickToHand(
      card,
      3,
      'Look at the top 3 cards of your deck. Reveal 1 (G Generation) Unit card that is Lv.3 to add to your hand.',
      (peeked) => peeked.type === 'unit' && (peeked.trait || '').includes('(G Generation)') && (parseInt(peeked.level) || 0) === 3,
      null
    );
  },
});

// ─── EB01-038 · G-Self ───────────────────────────────────────────────────
// [Deploy] Place 1 EX Resource.
registerCardEffect('EB01-038', {
  deploy: (card) => {
    placeExResource();
    addLog(`${card.name} — placed 1 EX Resource.`);
  },
});

// ─── EB01-029 · Gundam Astaroth Rinascimento (EX) ───────────────────────────
// [Deploy] If 5 or more enemy Units are in play, deal 2 damage to all Units
// with <Blocker> that are Lv.4 or lower.
registerCardEffect('EB01-029', {
  deploy: (card) => {
    if (state.oppField.filter(c => c).length < 5) return;
    const friendlyTargets = state.playerField.filter(c => c && hasNativeKeyword(c, 'Blocker') && (parseInt(c.level) || 0) <= 4);
    const enemyTargets = state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker') && (parseInt(c.level) || 0) <= 4);
    if (friendlyTargets.length === 0 && enemyTargets.length === 0) {
      addLog(`${card.name} — no Units with <Blocker> Lv.4 or lower in play. No effect.`);
      return;
    }
    friendlyTargets.forEach(t => { dealDamageToUnit(t, 2); addLog(`${card.name} — dealt 2 damage to ${t.name} (friendly).`); });
    enemyTargets.forEach(t => { dealDamageToUnit(t, 2, { fromEnemy: true }); addLog(`${card.name} — dealt 2 damage to ${t.name} (enemy).`); });
    render();
    setupDropZones();
  },
});

// Helper: show a "look at top card → return to top or bottom" modal
// Shared by GD01-039 Dopp, GD02-025 Gundam Heavyarms Lv4, and GD05-029
// Kayra's Re-GZ. Uses the same card-image + stats presentation as
// showTopDeckPeekPrompt (used by e.g. ST09-010 Minerva's [Deploy] peek)
// instead of the old bespoke text-only overlay, which never showed the
// card's image.
function showTopOrBottomModal(card, onDone) {
  const topPeek = peekTopOfDeck(1)[0];
  if (!topPeek) {
    addLog(`${card.name} — deck is empty.`);
    onDone?.(); return;
  }
  const { entry: topEntry, card: topCard } = topPeek;
  addLog(`${card.name} — looking at top card: ${topCard.name}.`);

  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const row = (label, val) =>
    `<div class="cp-row">
       <span class="cp-label">${label}</span>
       <span class="cp-val">${val ?? '—'}</span>
     </div>`;

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
    <div class="igm-title">Top of Deck</div>
    <div class="srb-content">
      <div class="cp-img-wrap">
        ${topCard.img ? `<img src="${topCard.img}" alt="${topCard.name}" />` : '<div class="cp-no-img">?</div>'}
      </div>
      <div class="cp-stats">
        <div class="cp-name">${topCard.name}</div>
        ${topCard.trait ? `<div class="cp-trait">${topCard.trait}</div>` : ''}
        <div class="cp-divider"></div>
        <div class="cp-stat-grid">
          ${row('Level', topCard.level)}
          ${row('Cost',  topCard.cost)}
          ${row('AP',    topCard.bp)}
          ${row('HP',    topCard.hp)}
          ${row('Type',  displayCardType(topCard.type))}
        </div>
        <div class="cp-effect">${topCard.effect ?? '—'}</div>
      </div>
    </div>
    <div class="igm-body">${card.name} — return ${topCard.name} to the top or bottom of your deck.</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-return-bottom">Return to Bottom</button>
      <button class="igm-btn igm-confirm-base">Return to Top</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });
  modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
    overlay.remove();
    addLog(`${card.name} — ${topCard.name} returned to top.`);
    onDone?.();
  });
  // Deliberately NOT .igm-cancel — see the identical rationale on
  // showLookTopCardPeekModal/the Shuji's Hideout modal: this is a
  // meaningful alternate choice, not a decline/skip, and must fall through
  // to the "Return to Top" default on auto-resolve instead of being
  // silently treated as the safe timeout action.
  modal.querySelector('.igm-return-bottom').addEventListener('click', () => {
    overlay.remove();
    sendDeckEntryToBottom(topEntry);
    addLog(`${card.name} — ${topCard.name} returned to bottom.`);
    onDone?.();
  });
}

// Helper: show a "look at top card → return to top or send to trash" modal.
// Same card-image + stats presentation as showTopOrBottomModal/
// showTopDeckPeekPrompt above (see their comments for why).
function showTopOrTrashModal(card, onDone) {
  const topPeek = peekTopOfDeck(1)[0];
  if (!topPeek) {
    addLog(`${card.name} — deck is empty.`);
    onDone?.(); return;
  }
  const { entry: topEntry, card: topCard } = topPeek;
  addLog(`${card.name} — looking at top card: ${topCard.name}.`);

  const overlay = document.createElement('div');
  overlay.id = 'ingame-menu-overlay';

  const row = (label, val) =>
    `<div class="cp-row">
       <span class="cp-label">${label}</span>
       <span class="cp-val">${val ?? '—'}</span>
     </div>`;

  const modal = document.createElement('div');
  modal.id = 'ingame-menu-modal';
  modal.innerHTML = `
    <button class="modal-hide-btn" title="Hide to view field"><span class="modal-hide-label">View Field</span><span class="material-symbols-outlined">ad_off</span></button>
    <div class="igm-title">Top of Deck</div>
    <div class="srb-content">
      <div class="cp-img-wrap">
        ${topCard.img ? `<img src="${topCard.img}" alt="${topCard.name}" />` : '<div class="cp-no-img">?</div>'}
      </div>
      <div class="cp-stats">
        <div class="cp-name">${topCard.name}</div>
        ${topCard.trait ? `<div class="cp-trait">${topCard.trait}</div>` : ''}
        <div class="cp-divider"></div>
        <div class="cp-stat-grid">
          ${row('Level', topCard.level)}
          ${row('Cost',  topCard.cost)}
          ${row('AP',    topCard.bp)}
          ${row('HP',    topCard.hp)}
          ${row('Type',  displayCardType(topCard.type))}
        </div>
        <div class="cp-effect">${topCard.effect ?? '—'}</div>
      </div>
    </div>
    <div class="igm-body">${card.name} — return ${topCard.name} to the top of your deck, or place it in your trash.</div>
    <div class="igm-btn-row">
      <button class="igm-btn igm-trash-alt">Place in Trash</button>
      <button class="igm-btn igm-confirm-base">Return to Top</button>
    </div>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  startSelectionTimer(overlay, () => autoResolveIgmModal(overlay));
  modal.querySelector('.modal-hide-btn').addEventListener('click', (e) => {
    e.stopPropagation();
    hideModalForFieldView(overlay);
  });
  modal.querySelector('.igm-confirm-base').addEventListener('click', () => {
    overlay.remove();
    addLog(`${card.name} — ${topCard.name} returned to top.`);
    onDone?.();
  });
  // Deliberately NOT .igm-cancel — see the identical rationale on
  // showLookTopCardPeekModal/showTopOrBottomModal: this is a meaningful
  // alternate choice, not a decline/skip, and must fall through to the
  // "Return to Top" default on auto-resolve instead of silently trashing
  // the card when the player doesn't act in time.
  modal.querySelector('.igm-trash-alt').addEventListener('click', () => {
    overlay.remove();
    topEntry.state = 'trash';
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    state.trashCount++;
    state.playerTrash.push(toTrashCard(topCard));
    NET.send({ type: 'DECK_MILL', card: topCard });
    broadcastCounters();
    addLog(`${card.name} — ${topCard.name} placed in trash.`);
    broadcastCardToast(`${topCard.name} was sent to trash.`);
    render();
    onDone?.();
  });
}

// ─── ST02-001 · Wing Gundam Lv6 ──────────────────────────────────────────────
// Breach 5 (native). This Unit may choose an active enemy Unit Lv.4 or lower.
registerCardEffect('ST02-001', {
  passiveKeywords: () => ['CanAttackActiveLv4'],
});

// ─── ST02-002 · Wing Gundam (Bird Mode) ──────────────────────────────────────
// [Deploy] Place 1 EX Resource.
registerCardEffect('ST02-002', {
  deploy: (card) => {
    placeExResource(false);
    addLog(`${card.name} — [Deploy] placed 1 EX Resource.`);
  },
});

// ─── ST02-003 · Gundam Heavyarms Lv5 ─────────────────────────────────────────
// [During Pair] During your turn, when this Unit destroys an enemy Unit with
// battle damage, deal 1 damage to all enemy Units that are Lv.3 or lower.
registerCardEffect('ST02-003', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (destroyerSide !== 'player' || !destroyerIsAttacker || !state.myTurn) return;
    if (!card.pilot) return;
    const targets = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (targets.length === 0) return;
    addLog(`${card.name} — [During Pair] destroyed enemy: deal 1 damage to all enemy Units Lv.3 or lower.`);
    targets.forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
    render();
    setupDropZones();
  },
});

// ─── ST03-006 · Char's Zaku II Lv3 ──────────────────────────────────────────
// [Destroyed] Look at top 3, may reveal 1 (Zeon)/(Neo Zeon) Unit → hand.
registerCardEffect('ST03-006', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    lookAtTopAndPickToHand(
      card, 3,
      'Look at top 3 cards. Reveal 1 (Zeon)/(Neo Zeon) Unit to add to your hand.',
      c => (c.type === 'unit' || c.type === 'unit-token') &&
           ((c.trait || '').includes('(Zeon)') || (c.trait || '').includes('(Neo Zeon)'))
    );
  },
});

// ─── ST03-008 · Zaku II Lv2 ──────────────────────────────────────────────────
// [Attack] This Unit gets AP+2 during this turn.
registerCardEffect('ST03-008', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    unit.tempApMod = (unit.tempApMod || 0) + 2;
    NET.send({ type: 'FIELD_AP_MOD', uids: [unit.uid], delta: 2, field: 'tempApMod' });
    addLog(`${card.name} — [Attack] gets AP+2 this turn.`);
    render();
    ctx?.onDone?.();
  },
});

// ─── ST03-009 · Gouf Lv3 ─────────────────────────────────────────────────────
// [Deploy] Deploy 1 rested Zaku II token.
registerCardEffect('ST03-009', {
  deploy: (card) => {
    deployTokenToField('T-007', { rested: true });
    addLog(`${card.name} — [Deploy] deployed 1 rested Zaku II token.`);
  },
});

// ─── ST06-005 · Red Gundam Lv4 ───────────────────────────────────────────────
// Breach 1 (native). [Attack] Choose 1 to 2 friendly (Clan) Units. They get
// AP+2 during this turn.
registerCardEffect('ST06-005', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Clan)'));
    if (candidates.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModalMulti(
      card.name,
      'Choose 1–2 friendly (Clan) Units. They get AP+2 this turn.',
      candidates,
      { min: 1, max: 2, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length === 0) { ctx?.onDone?.(); return; }
        chosen.forEach(t => {
          t.tempApMod = (t.tempApMod || 0) + 2;
          NET.send({ type: 'FIELD_AP_MOD', uids: [t.uid], delta: 2, field: 'tempApMod' });
        });
        addLog(`${card.name} — [Attack] ${chosen.map(t => t.name).join(', ')} get AP+2 this turn.`);
        render();
        ctx?.onDone?.();
      }
    );
  },
});

// ─── ST06-007 · Ortega's Rick Dom ────────────────────────────────────────────
// [Deploy] Choose 1 of your other (Clan) Units. During this turn, it may choose
// an active enemy Unit with 3 or less AP as its attack target.
registerCardEffect('ST06-007', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && c.uid !== card.uid && (c.trait || '').includes('(Clan)'));
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 other (Clan) Unit. It may choose active enemy Units with AP≤3 this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('CanAttackActiveAP3');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP3' });
        addLog(`${card.name} — ${target.name} can attack active enemy Units with AP≤3 this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── ST07-005 · Gundam Dynames Lv4 ───────────────────────────────────────────
// During your turn, when this Unit destroys an enemy Unit with battle damage,
// this Unit recovers 2 HP. [During Link] This Unit gets AP+2.
registerCardEffect('ST07-005', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (destroyerSide !== 'player' || !destroyerIsAttacker || !state.myTurn) return;
    // Deferred — see deferredApplyHpMod's comment in white.js: this fires
    // before the Unit's own counter-damage from this same exchange is
    // applied, which would otherwise cap the recovery to 0 whenever the
    // Unit was undamaged going into the battle.
    setTimeout(() => {
      const before = effectiveHp(card);
      applyHpMod([card], 2);
      const healed = effectiveHp(card) - before;
      if (healed <= 0) return;
      addLog(`${card.name} — destroyed enemy: recovered ${healed} HP (now: ${effectiveHp(card)}).`);
      render();
    }, 0);
  },
  passiveUnitApBonus: (card) => isLinkUnit(card) ? 2 : 0,
});

// ─── ST07-007 · Gundam Kyrios Lv3 ────────────────────────────────────────────
// During your turn, while you have a (CB) Pilot in play, this Unit gets AP+2.
registerCardEffect('ST07-007', {
  passiveUnitApBonus: (card, ownerSide) => {
    const isOwnerTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    if (!isOwnerTurn) return 0;
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    return field.some(c => c?.pilot && (c.pilot.trait || '').includes('(CB)')) ? 2 : 0;
  },
});

// ─── GD01-023 · Char's Gelgoog ───────────────────────────────────────────────
// [Activate･Main] Discard 1 (Zeon)/(Neo Zeon) Unit card from your hand:
// If no pilot is paired with this Unit, choose 1 (Newtype) Pilot Lv.3 or lower
// from your trash, and pair it with this Unit.
registerCardEffect('GD01-023', {
  canActivate: (card) => {
    if (!state.myTurn || state.phase !== 'Main Phase') return false;
    if (card.pilot) return false;
    const hasZeonInHand = state.hand.some(c =>
      (c.type === 'unit' || c.type === 'unit-token') &&
      ((c.trait || '').includes('(Zeon)') || (c.trait || '').includes('(Neo Zeon)'))
    );
    const hasNewtypePilot = state.playerTrash.some(c =>
      (c.type === 'pilot' || c.type === 'command pilot') &&
      (c.trait || '').includes('(Newtype)') && (parseInt(c.level) || 0) <= 3
    );
    return hasZeonInHand && hasNewtypePilot;
  },
  activate: (card, ctx) => {
    const slot = state.playerField.findIndex(c => c && c.uid === card.uid);
    if (slot === -1) { ctx?.onDone?.(); return; }
    if (card.pilot) {
      addLog(`${card.name} — already paired; effect cancelled.`);
      ctx?.onDone?.(); return;
    }
    const zeonHandUnits = state.hand.filter(c =>
      (c.type === 'unit' || c.type === 'unit-token') &&
      ((c.trait || '').includes('(Zeon)') || (c.trait || '').includes('(Neo Zeon)'))
    );
    if (zeonHandUnits.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModal(
      card.name,
      'Discard 1 (Zeon)/(Neo Zeon) Unit from your hand to pair a (Newtype) Pilot Lv.3 or lower from trash.',
      zeonHandUnits,
      (discarded) => {
        if (!discarded) { ctx?.onDone?.(); return; }
        state.hand = state.hand.filter(c => c.uid !== discarded.uid);
        updateTempState(discarded.cardNo, discarded.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(discarded));
        broadcastLog(`${card.name} — discarded ${discarded.name}.`);
        broadcastCardToast(`${discarded.name} was discarded due to ${card.name} effect.`);
        const newtypePilots = state.playerTrash.filter(c =>
          (c.type === 'pilot' || c.type === 'command pilot') &&
          (c.trait || '').includes('(Newtype)') && (parseInt(c.level) || 0) <= 3
        );
        if (newtypePilots.length === 0) {
          addLog(`${card.name} — no (Newtype) Pilot Lv.3 or lower in trash.`);
          broadcastCounters(); render(); ctx?.onDone?.(); return;
        }
        const pilotCards = newtypePilots.map(c => {
          const db = cardDb[c.cardNo];
          return db ? buildGameCardFromDb(db, c.copyVer) : { ...c };
        });
        showCardPickerModal(
          card.name,
          'Choose 1 (Newtype) Pilot Lv.3 or lower from your trash to pair.',
          pilotCards,
          (chosenPilot) => {
            if (!chosenPilot) { broadcastCounters(); render(); ctx?.onDone?.(); return; }
            const trashIdx = state.playerTrash.findIndex(c =>
              c.cardNo === chosenPilot.cardNo && c.copyVer === chosenPilot.copyVer
            );
            if (trashIdx !== -1) {
              state.playerTrash.splice(trashIdx, 1);
              state.trashCount = Math.max(0, state.trashCount - 1);
            }
            const pairedUnit = { ...card, pilot: chosenPilot };
            if (isLinkUnit(pairedUnit)) {
              pairedUnit.linkedThisTurn = true;
              // Bug fix: this card is normally deployed and activated in the
              // same turn (discard cost + trash-pair are both Main Phase
              // actions available right after playing it), so it's still
              // carrying deploy sickness (tempCantAttack) at this point.
              // Newly satisfying the Link requirement removes that sickness
              // — same rule the standard PAIR_PILOT action applies (game.js)
              // — but this card pairs inline instead of going through that
              // action, so it has to clear the flag itself.
              pairedUnit.tempCantAttack = false;
              if (state.playerBase) {
                window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, pairedUnit, slot);
              }
            }
            state.playerField[slot] = pairedUnit;
            addLog(`${card.name} — paired ${chosenPilot.name} (from trash).`);
            triggerCardEffect(chosenPilot, 'pair', { unit: pairedUnit, slot });
            triggerCardEffect(pairedUnit, 'pair', { unit: pairedUnit, slot });
            NET.send({ type: 'PLAY_CARD', slot, card: state.playerField[slot], counters: myCounters() });
            broadcastCounters(); render(); setupDropZones();
            ctx?.onDone?.();
          },
          card, { mandatory: true }
        );
      },
      card, { mandatory: true }
    );
  },
});

// ─── GD01-024 · Wing Gundam Zero ─────────────────────────────────────────────
// High-Maneuver (native). [Deploy] Deal 3 damage to all Units Lv.5 or lower
// (both sides — raw card text has no "enemy" qualifier).
registerCardEffect('GD01-024', {
  deploy: (card) => {
    const lv5 = (c) => c && (parseInt(c.level) || 0) <= 5;
    const ownTargets = state.playerField.filter(c => c !== card && lv5(c));
    const oppTargets  = state.oppField.filter(lv5);
    if (ownTargets.length === 0 && oppTargets.length === 0) return;
    addLog(`${card.name} — [Deploy] deal 3 damage to all Units Lv.5 or lower.`);
    ownTargets.forEach(t => dealDamageToUnit(t, 3));
    oppTargets.forEach(t => dealDamageToUnit(t, 3, { fromEnemy: true }));
    render();
    setupDropZones();
  },
});

// ─── GD01-025 · Gundam Deathscythe ───────────────────────────────────────────
// [When Paired·(Operation Meteor) Pilot] Place 1 rested Resource. Then, this
// Unit gains <First Strike> during this turn.
registerCardEffect('GD01-025', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !unit.pilot) return;
    if (!(unit.pilot.trait || '').includes('(Operation Meteor)')) return;
    state.resBase = Math.min(state.resBase + 1, 10);
    addLog(`${card.name} — [When Paired·(Op Meteor) Pilot] placed 1 rested Resource (level: ${state.resBase + state.exRes + state.exResRested}).`);
    unit.tempKeywords = unit.tempKeywords || [];
    if (!unit.tempKeywords.includes('First Strike')) unit.tempKeywords.push('First Strike');
    NET.send({ type: 'TEMP_KEYWORD_ADD', uid: unit.uid, keyword: 'First Strike' });
    addLog(`${card.name} — ${unit.name} gains <First Strike> this turn.`);
    broadcastCounters();
    render();
    setupDropZones();
  },
});

// ─── GD01-026 · Char's Zaku II Lv3 (pilot version) ──────────────────────────
// [During Pair][Destroyed] Deploy 1 rested Char's Zaku II token.
// The destroyed handler fires on the unit card object which retains its pilot
// field even after the slot is nulled.
registerCardEffect('GD01-026', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    if (!card.pilot) return;
    deployTokenToField('T-006', { rested: true });
    addLog(`${card.name} — [During Pair][Destroyed] deployed 1 rested Char's Zaku II token.`);
  },
});

// ─── GD01-027 · Big Zam ──────────────────────────────────────────────────────
// Breach 4 (native). [Deploy] If you have 10 or more (Zeon)/(Neo Zeon) Unit
// cards in your trash, deal 4 damage to all enemy Units with Blocker.
registerCardEffect('GD01-027', {
  deploy: (card) => {
    const zeonTrashCount = state.playerTrash.filter(c =>
      (c.type === 'unit' || c.type === 'unit-token') &&
      ((c.trait || '').includes('(Zeon)') || (c.trait || '').includes('(Neo Zeon)'))
    ).length;
    if (zeonTrashCount < 10) return;
    const targets = state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker'));
    if (targets.length === 0) return;
    addLog(`${card.name} — [Deploy] 10+ (Zeon)/(Neo Zeon) in trash: deal 4 damage to all Blocker enemies.`);
    targets.forEach(t => dealDamageToUnit(t, 4, { fromEnemy: true }));
    render();
    setupDropZones();
  },
});

// ─── GD01-028 · Gundam Sandrock Lv5 ─────────────────────────────────────────
// [Deploy] You may deploy 1 (Maganac Corps) Unit card from your hand.
registerCardEffect('GD01-028', {
  deploy: (card) => {
    const candidates = state.hand.filter(c =>
      (c.type === 'unit' || c.type === 'unit-token') &&
      (c.trait || '').includes('(Maganac Corps)')
    );
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'You may deploy 1 (Maganac Corps) Unit from your hand.',
      candidates,
      (target) => {
        if (!target) return;
        const slot = state.playerField.findIndex(c => !c);
        if (slot === -1) { addLog('Field is full — cannot deploy.'); return; }
        state.hand = state.hand.filter(c => c.uid !== target.uid);
        updateTempState(target.cardNo, target.copyVer, 'field');
        state.playerField[slot] = target;
        target.tempCantAttack = true;
        addLog(`${card.name} — [Deploy] deployed ${target.name}.`);
        triggerCardEffect(target, 'deploy', { slot });
        NET.send({ type: 'PLAY_CARD', slot, card: state.playerField[slot], counters: myCounters() });
        render();
        setupDropZones();
      },
      card,
      { mandatory: false, skipLabel: 'Skip' }
    );
  },
});

// ─── GD01-029 · Shenlong Gundam Lv5 ─────────────────────────────────────────
// Breach 4 (native). [Attack] Choose 1 enemy Unit with Blocker that is Lv.3 or
// lower. Destroy it.
registerCardEffect('GD01-029', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker') && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with Blocker that is Lv.3 or lower. Destroy it.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        dealDamageToUnit(target, effectiveHp(target) + 99, { fromEnemy: true });
        addLog(`${card.name} — [Attack] destroyed ${target.name}.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-032 · Gyan ─────────────────────────────────────────────────────────
// [When Paired·(Zeon) Pilot] Choose 1 enemy Unit with Blocker that is Lv.2 or
// lower. Destroy it.
registerCardEffect('GD01-032', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !unit.pilot) return;
    if (!(unit.pilot.trait || '').includes('(Zeon)')) return;
    const candidates = state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker') && (parseInt(c.level) || 0) <= 2);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      '[When Paired·(Zeon) Pilot] Choose 1 enemy Blocker Lv.2 or lower. Destroy it.',
      candidates,
      (target) => {
        if (!target) return;
        dealDamageToUnit(target, effectiveHp(target) + 99, { fromEnemy: true });
        addLog(`${card.name} — [When Paired·(Zeon) Pilot] destroyed ${target.name}.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD01-034 · Gundam Heavyarms Lv4 ─────────────────────────────────────────
// [During Pair] This Unit gains <Breach 3>.
// The effect text "Breach 3" is in the unit's own effect, but it should only
// apply while a pilot is paired. Use conditionalBreachValue to gate it.
registerCardEffect('GD01-034', {
  conditionalBreachValue: (card) => card.pilot ? 3 : 0,
});

// ─── GD01-038 · Adzam ────────────────────────────────────────────────────────
// [Deploy] If you have 5 or more enemy Units in play, deal 1 damage to all
// enemy Units.
registerCardEffect('GD01-038', {
  deploy: (card) => {
    const enemyCount = state.oppField.filter(c => c).length;
    if (enemyCount < 5) return;
    addLog(`${card.name} — [Deploy] 5+ enemy Units: deal 1 damage to all enemy Units.`);
    state.oppField.filter(c => c).forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
    render();
    setupDropZones();
  },
});

// ─── GD01-039 · Dopp ─────────────────────────────────────────────────────────
// [Deploy] Look at the top card of your deck. Return it to the top or bottom.
registerCardEffect('GD01-039', {
  deploy: (card) => showTopOrBottomModal(card),
});

// ─── GD01-042 · Duo's Leo ────────────────────────────────────────────────────
// This Unit may choose an active enemy Unit that is Lv.2 or lower as its
// attack target. (Requires [2,3,4,5] extension in setupDropZones.)
registerCardEffect('GD01-042', {
  passiveKeywords: () => ['CanAttackActiveLv2'],
});

// ─── GD01-043 · Rascardo's Maganac ───────────────────────────────────────────
// [Deploy] Choose 1 of your green Units. During this turn, it may choose an
// active enemy Unit with 4 or less AP as its attack target.
registerCardEffect('GD01-043', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.color || '').toLowerCase() === 'green');
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 of your green Units. It may choose active enemy Units with AP≤4 this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        target.tempKeywords.push('CanAttackActiveAP4');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP4' });
        addLog(`${card.name} — ${target.name} can attack active enemy Units with AP≤4 this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-020 · Elmeth ───────────────────────────────────────────────────────
// [Deploy] Look at the top 5 cards of your deck. You may reveal 1 green (Zeon)
// Pilot and add it to your hand. Return the rest randomly to the bottom.
// [During Link] This Unit gets AP+2.
registerCardEffect('GD02-020', {
  deploy: (card) => {
    lookAtTopAndPickToHand(
      card, 5,
      'Look at top 5 cards. Reveal 1 green (Zeon) Pilot to add to your hand.',
      c => (c.color || '').toLowerCase() === 'green' &&
           (c.type === 'pilot' || c.type === 'command pilot') &&
           (c.trait || '').includes('(Zeon)')
    );
  },
  passiveUnitApBonus: (card) => isLinkUnit(card) ? 2 : 0,
});

// ─── GD02-021 · Gundam AGE-1 Normal Lv3 ──────────────────────────────────────
// [Deploy] You may discard 1 green (Earth Federation) Unit from your hand. If
// you do, place 1 EX Resource. Then, if you are Lv.7 or higher, draw 1.
registerCardEffect('GD02-021', {
  deploy: (card) => {
    const candidates = state.hand.filter(c =>
      (c.type === 'unit' || c.type === 'unit-token') &&
      (c.color || '').toLowerCase() === 'green' &&
      (c.trait || '').includes('(Earth Federation)')
    );
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'You may discard 1 green (Earth Federation) Unit to place 1 EX Resource (draw 1 if Lv.7+).',
      candidates,
      (target) => {
        if (!target) return;
        state.hand = state.hand.filter(c => c.uid !== target.uid);
        updateTempState(target.cardNo, target.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(target));
        broadcastLog(`${card.name} — discarded ${target.name}: placed 1 EX Resource.`);
        broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
        placeExResource(false);
        if (state.playerLevel >= 7) {
          const drawn = drawCardsFromTempDeck(1, true);
          drawn.forEach(c => state.hand.push(c));
          addLog(`${card.name} — Lv.7+: drew 1 card.`);
        }
        render();
      },
      card,
      { mandatory: false, skipLabel: 'Skip' }
    );
  },
});

// ─── GD02-022 · G-Exes ───────────────────────────────────────────────────────
// [Once per Turn] When you place an EX Resource, choose 1 (AGE System) Unit.
// It gains <Breach 2> during this turn.
registerCardEffect('GD02-022', {
  onExResourcePlaced: (card) => {
    if (!state.myTurn || card.passiveUsedThisTurn) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(AGE System)'));
    if (candidates.length === 0) return;
    card.passiveUsedThisTurn = true;
    showCardPickerModal(
      card.name,
      '[Once per Turn] EX Resource placed: choose 1 (AGE System) Unit. It gains <Breach 2> this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Breach 2')) target.tempKeywords.push('Breach 2');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 2' });
        addLog(`${card.name} — ${target.name} gains <Breach 2> this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-023 · Gundam AGE-1 Spallow ─────────────────────────────────────────
// [During Link] While you are Lv.7 or higher, this Unit gains <First Strike>.
registerCardEffect('GD02-023', {
  passiveKeywords: (card) =>
    (isLinkUnit(card) && state.playerLevel >= 7) ? ['First Strike'] : [],
});

// ─── GD02-024 · Red Gundam Lv5 ───────────────────────────────────────────────
// [During Link] This Unit gains <High-Maneuver>.
registerCardEffect('GD02-024', {
  passiveKeywords: (card) => isLinkUnit(card) ? ['High-Maneuver'] : [],
});

// ─── GD02-025 · Gundam Heavyarms Lv4 ─────────────────────────────────────────
// [Deploy] Look at the top card of your deck. Return it to the top or bottom.
registerCardEffect('GD02-025', {
  deploy: (card) => showTopOrBottomModal(card),
});

// ─── GD02-026 · Genoace Custom ───────────────────────────────────────────────
// [Deploy] If you are Lv.7 or higher, choose 1 (AGE System) Unit. It gets
// AP+2 during this turn.
registerCardEffect('GD02-026', {
  deploy: (card) => {
    if (state.playerLevel < 7) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(AGE System)'));
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Lv.7+: Choose 1 (AGE System) Unit. It gets AP+2 this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempApMod = (target.tempApMod || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 2, field: 'tempApMod' });
        addLog(`${card.name} — ${target.name} gets AP+2 this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD02-031 · Gundam AGE-1 Titus Lv4 ──────────────────────────────────────
// While you are Lv.7 or higher, this Unit gets AP+2.
registerCardEffect('GD02-031', {
  passiveUnitApBonus: (card) => {
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const lvl = ownerSide === 'player'
      ? state.playerLevel
      : (state.oppResBase || 0) + (state.oppExRes || 0) + (state.oppExResRested || 0);
    return lvl >= 7 ? 2 : 0;
  },
});

// ─── GD02-033 · Kikeroga (MA Mode) ───────────────────────────────────────────
// While you have another friendly (Zeon) Link Unit in play, this Unit gains
// Breach 5.
registerCardEffect('GD02-033', {
  conditionalBreachValue: (card) => {
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const field = state[`${ownerSide}Field`];
    const hasZeonLink = field.some(c =>
      c && c.uid !== card.uid && (c.trait || '').includes('(Zeon)') && isLinkUnit(c)
    );
    return hasZeonLink ? 5 : 0;
  },
});

// ─── GD02-034 · GQuuuuuuX ────────────────────────────────────────────────────
// [During Pair·Red Pilot] This Unit gets AP+2.
registerCardEffect('GD02-034', {
  passiveUnitApBonus: (card) =>
    (card.pilot && (card.pilot.color || '').toLowerCase() === 'red') ? 2 : 0,
});

// ─── GD02-035 · Police Zaku ──────────────────────────────────────────────────
// This Unit can't choose the enemy player as its attack target.
// Per ruling: treated as cantAttackNonUnit (blocks base, shield, and direct).
registerCardEffect('GD02-035', {
  cantAttackNonUnit: () => true,
});

// ─── GD03-017 · Kämpfer ──────────────────────────────────────────────────────
// [Burst] Choose 1 (Cyclops Team) Pilot card from your trash. Add it to hand.
// [When Paired·(Cyclops Team) Pilot] All (Cyclops Team) Units may choose active
// enemy Units with 5 or less AP as their attack targets during this turn.
registerCardEffect('GD03-017', {
  burst: (card) => {
    const pilots = state.playerTrash.filter(c =>
      (c.type === 'pilot' || c.type === 'command pilot') &&
      (c.trait || '').includes('(Cyclops Team)')
    );
    if (pilots.length === 0) { addLog(`${card.name} — [Burst] no (Cyclops Team) Pilot in trash.`); return; }
    const pilotCards = pilots.map(c => {
      const db = cardDb[c.cardNo];
      return db ? buildGameCardFromDb(db, c.copyVer) : { ...c };
    });
    showCardPickerModal(
      card.name,
      '[Burst] Choose 1 (Cyclops Team) Pilot from your trash to add to your hand.',
      pilotCards,
      (chosen) => {
        if (!chosen) return;
        const trashIdx = state.playerTrash.findIndex(c =>
          c.cardNo === chosen.cardNo && c.copyVer === chosen.copyVer
        );
        if (trashIdx !== -1) {
          state.playerTrash.splice(trashIdx, 1);
          state.trashCount = Math.max(0, state.trashCount - 1);
        }
        const handCard = buildGameCardFromDb(cardDb[chosen.cardNo] || chosen, chosen.copyVer);
        state.hand.push(handCard);
        updateTempState(chosen.cardNo, chosen.copyVer, 'hand');
        addLog(`${card.name} — [Burst] added ${chosen.name} to hand.`);
        broadcastCounters();
        render();
      },
      card,
      { mandatory: true }
    );
  },
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !unit.pilot) return;
    if (!(unit.pilot.trait || '').includes('(Cyclops Team)')) return;
    const cyclopsUnits = state.playerField.filter(c => c && (c.trait || '').includes('(Cyclops Team)'));
    cyclopsUnits.forEach(t => {
      t.tempKeywords = t.tempKeywords || [];
      if (!t.tempKeywords.includes('CanAttackActiveAP5')) t.tempKeywords.push('CanAttackActiveAP5');
      NET.send({ type: 'TEMP_KEYWORD_ADD', uid: t.uid, keyword: 'CanAttackActiveAP5' });
    });
    if (cyclopsUnits.length > 0) {
      addLog(`${card.name} — [When Paired·(Cyclops Team)] all (Cyclops Team) Units can attack active enemies with AP≤5 this turn.`);
      render();
      setupDropZones();
    }
  },
});

// ─── GD03-018 · Altron Gundam ─────────────────────────────────────────────────
// [Attack] Choose 1 enemy Unit with Blocker. Deal 5 damage to it.
registerCardEffect('GD03-018', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker'));
    if (candidates.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModal(
      card.name,
      '[Attack] Choose 1 enemy Unit with Blocker. Deal 5 damage to it.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        dealDamageToUnit(target, 5, { fromEnemy: true });
        addLog(`${card.name} — [Attack] dealt 5 damage to ${target.name}.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-019 · Gundam AGE-2 Normal ──────────────────────────────────────────
// [During Pair] Enemy Units choose this rested Unit as their attack target if
// possible when attacking.
// [When Linked] Place 1 EX Resource.
registerCardEffect('GD03-019', {
  forcedAttackTarget: (card, defenderSide) => !!card.pilot && card.rested,
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    placeExResource(false);
    addLog(`${card.name} — [When Linked] placed 1 EX Resource.`);
  },
});

// ─── GD03-020 · Zaku II FZ ───────────────────────────────────────────────────
// [When Paired] If you have 4 or more (Cyclops Team) Unit cards / Command cards
// in your trash, deploy 2 rested Ad Balloon tokens.
// While you have a Unit with "Ad Balloon" in its card name in play, this Unit
// can't receive enemy battle damage.
registerCardEffect('GD03-020', {
  pair: (card, ctx) => {
    const cyclopsTrash = state.playerTrash.filter(c =>
      ((c.type === 'unit' || c.type === 'unit-token') ||
       c.type === 'command' || c.type === 'command pilot') &&
      (c.trait || '').includes('(Cyclops Team)')
    ).length;
    if (cyclopsTrash < 4) return;
    addLog(`${card.name} — [When Paired] 4+ (Cyclops Team) cards in trash: deploy 2 rested Ad Balloon tokens.`);
    deployTokenToField('T-014', { rested: true });
    deployTokenToField('T-014', { rested: true });
    render();
    setupDropZones();
  },
  passiveUnitBattleDamageImmune: (card, attacker) => {
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    return state[`${ownerSide}Field`].some(c => c && (c.name || '').includes('Ad Balloon'));
  },
});

// ─── GD03-021 · Gundam Deathscythe Hell ──────────────────────────────────────
// [Deploy] Choose 1 (Operation Meteor)/(G Team) Unit. During this turn, it may
// choose an active enemy Unit as its attack target.
registerCardEffect('GD03-021', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c =>
      c && ((c.trait || '').includes('(Operation Meteor)') || (c.trait || '').includes('(G Team)'))
    );
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 (Operation Meteor)/(G Team) Unit. It may choose any active enemy Unit this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveAll')) target.tempKeywords.push('CanAttackActiveAll');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAll' });
        addLog(`${card.name} — ${target.name} can attack any active enemy Unit this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-022 · Gundam Kyrios Lv5 ────────────────────────────────────────────
// [During Link] During your turn, when this Unit destroys an enemy Unit with
// battle damage, deal 1 damage to all enemy Units Lv.3 or lower.
registerCardEffect('GD03-022', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (destroyerSide !== 'player' || !destroyerIsAttacker || !state.myTurn) return;
    if (!isLinkUnit(card)) return;
    const targets = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (targets.length === 0) return;
    addLog(`${card.name} — [During Link] destroyed enemy: deal 1 damage to all enemy Units Lv.3 or lower.`);
    targets.forEach(t => dealDamageToUnit(t, 1, { fromEnemy: true }));
    render();
    setupDropZones();
  },
});

// ─── GD03-023 · G-Bouncer ────────────────────────────────────────────────────
// [Once per Turn] When you place an EX Resource, choose 1 (AGE System) Unit.
// It gains <High-Maneuver> during this turn.
registerCardEffect('GD03-023', {
  onExResourcePlaced: (card) => {
    if (!state.myTurn || card.passiveUsedThisTurn) return;
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(AGE System)'));
    if (candidates.length === 0) return;
    card.passiveUsedThisTurn = true;
    showCardPickerModal(
      card.name,
      '[Once per Turn] EX placed: choose 1 (AGE System) Unit. It gains <High-Maneuver> this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('High-Maneuver')) target.tempKeywords.push('High-Maneuver');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'High-Maneuver' });
        addLog(`${card.name} — ${target.name} gains <High-Maneuver> this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD03-024 · Hy-Gogg ──────────────────────────────────────────────────────
// [When Linked] If you have another (Cyclops Team) Unit in play, deploy 1
// rested Hy-Gogg token.
registerCardEffect('GD03-024', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const hasCyclops = state.playerField.some(c => c && c.uid !== unit.uid && (c.trait || '').includes('(Cyclops Team)'));
    if (!hasCyclops) return;
    deployTokenToField('T-013', { rested: true });
    addLog(`${card.name} — [When Linked] another (Cyclops Team) Unit in play: deployed 1 rested Hy-Gogg token.`);
  },
});

// ─── GD03-025 · Gundam Sandrock Custom ───────────────────────────────────────
// Enemy Units choose one of your rested (Maganac Corps) Units as their attack
// target if possible when attacking.
registerCardEffect('GD03-025', {
  forcedAttackTargetPool: (card, defenderSide) => {
    const field = state[`${defenderSide}Field`];
    return field.filter(c => c && c.rested && (c.trait || '').includes('(Maganac Corps)'));
  },
});

// ─── GD03-028 · Auda's Maganac ───────────────────────────────────────────────
// [Attack] If this Unit is attacking an enemy Unit, this Unit gets AP+2 during
// this battle. (battleApMod is captured by effectiveBp() before runDamageStep
// clears it, so setting it in the attack trigger is safe and correct.)
registerCardEffect('GD03-028', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (ctx.isShield || ctx.isDirect) { ctx?.onDone?.(); return; }
    const isUnitTarget = state.oppField.some(c => c && c.uid === ctx.defender?.uid);
    if (!isUnitTarget) { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    unit.battleApMod = (unit.battleApMod || 0) + 2;
    NET.send({ type: 'FIELD_AP_MOD', uids: [unit.uid], delta: 2, field: 'battleApMod' });
    addLog(`${card.name} — [Attack] attacking unit: AP+2 this battle.`);
    ctx?.onDone?.();
  },
});

// ─── GD03-029 · Gundam Heavyarms Custom ──────────────────────────────────────
// During your turn, when this Unit destroys an enemy Unit with battle damage,
// deal 2 damage to all enemy Units with Blocker.
registerCardEffect('GD03-029', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (destroyerSide !== 'player' || !destroyerIsAttacker || !state.myTurn) return;
    const targets = state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker'));
    if (targets.length === 0) return;
    addLog(`${card.name} — destroyed enemy: deal 2 damage to all enemy Blockers.`);
    targets.forEach(t => dealDamageToUnit(t, 2, { fromEnemy: true }));
    render();
    setupDropZones();
  },
});

// ─── GD03-030 · Gundam Kyrios (Tail Unit) ─────────────────────────────────────
// While you have a (CB) Link Unit in play, this card in your hand gets cost −1.
registerCardEffect('GD03-030', {
  handCostReduction: (card) => {
    return state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('(CB)')) ? 1 : 0;
  },
});

// ─── EB01-039 · Rising Freedom Gundam ─────────────────────────────────────────
// While playing this card from your hand, if 3 or more enemy Units are in
// play, play it as if it has 3 Lv. and cost (base is Lv.6, cost 5).
// Pure condition-based hand discount — no discard/sacrifice cost — same
// convention as ST08-001 (handLevelReduction + handCostReduction only).
registerCardEffect('EB01-039', {
  handLevelReduction: (card) => {
    if (state.oppField.filter(c => c).length < 3) return 0;
    return Math.max(0, (parseInt(card.level) || 0) - 3);
  },
  handCostReduction: (card) => {
    if (state.oppField.filter(c => c).length < 3) return 0;
    return Math.max(0, (parseInt(card.cost) || 0) - 3);
  },
});

// ─── GD04-017 · Zeong ─────────────────────────────────────────────────────────
// [When Paired·(Newtype) Pilot] Deploy 2 Wire-Guided Arm tokens.
// [Destroyed] Deploy 1 rested Zeong (Head) token.
registerCardEffect('GD04-017', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !unit.pilot) return;
    if (!(unit.pilot.trait || '').includes('(Newtype)')) return;
    addLog(`${card.name} — [When Paired·(Newtype) Pilot] deploying 2 Wire-Guided Arm tokens.`);
    deployTokenToField('T-022');
    deployTokenToField('T-022');
    render();
    setupDropZones();
  },
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    deployTokenToField('T-023', { rested: true });
    addLog(`${card.name} — [Destroyed] deployed 1 rested Zeong (Head) token.`);
  },
});

// ─── GD04-018 · Gundam Pharact ────────────────────────────────────────────────
// Breach 5 (native). [Once per Turn] During your turn, when one of your other
// (Academy) Units receives damage from an enemy, place 1 EX Resource.
// Reacts to both battle damage and effect damage from enemy.
registerCardEffect('GD04-018', {
  onAnyFriendlyUnitTakesBattleDamage: (card, damagedUnit, attackerSide) => {
    if (!state.myTurn || card.passiveUsedThisTurn) return;
    if (card.uid === damagedUnit.uid) return;
    if (!(damagedUnit.trait || '').includes('(Academy)')) return;
    card.passiveUsedThisTurn = true;
    placeExResource(false);
    addLog(`${card.name} — [Once per Turn] another (Academy) Unit took battle damage: placed 1 EX Resource.`);
  },
  onAnyFriendlyUnitTakesEffectDamage: (card, damagedUnit) => {
    if (!state.myTurn || card.passiveUsedThisTurn) return;
    if (card.uid === damagedUnit.uid) return;
    if (!(damagedUnit.trait || '').includes('(Academy)')) return;
    card.passiveUsedThisTurn = true;
    placeExResource(false);
    addLog(`${card.name} — [Once per Turn] another (Academy) Unit took effect damage from enemy: placed 1 EX Resource.`);
  },
});

// ─── GD04-019 · GN Armor Type-D ──────────────────────────────────────────────
// Breach 3 (native). [Destroyed] Look at the top 3 cards of your deck. You may
// reveal 1 (CB) Unit Lv.5 or lower and add it to your hand.
registerCardEffect('GD04-019', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    lookAtTopAndPickToHand(
      card, 3,
      'Look at top 3 cards. Reveal 1 (CB) Unit Lv.5 or lower to add to your hand.',
      c => (c.type === 'unit' || c.type === 'unit-token') &&
           (c.trait || '').includes('(CB)') && (parseInt(c.level) || 0) <= 5
    );
  },
});

// ─── GD04-020 · Gundam Lfrith Ur ─────────────────────────────────────────────
// [Once per Turn] During your turn, when you play and activate a (Dawn of Fold)
// Command card using an EX Resource, draw 1 card.
registerCardEffect('GD04-020', {
  onDawnCommandWithExPlayed: (card, commandCard, onDone) => {
    if (!state.myTurn || card.passiveUsedThisTurn) { onDone(); return; }
    card.passiveUsedThisTurn = true;
    const drawn = drawCardsFromTempDeck(1, true);
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — [Once per Turn] (Dawn of Fold) Command played with EX: drew 1 card.`);
    render();
    onDone();
  },
});

// ─── GD04-021 · Gundam Lfrith Thorn ──────────────────────────────────────────
// Breach 3 (native). During your turn, when you play and activate a (Dawn of
// Fold) Command card using an EX Resource, you may pair a (Dawn of Fold)
// Command Pilot card from your trash with one of your Units with "Gundam
// Lfrith" in its card name.
registerCardEffect('GD04-021', {
  onDawnCommandWithExPlayed: (card, commandCard, onDone) => {
    if (!state.myTurn) { onDone(); return; }
    const dofPilots = state.playerTrash.filter(c =>
      (c.type === 'command pilot') &&
      (c.trait || '').includes('(Dawn of Fold)')
    );
    if (dofPilots.length === 0) { onDone(); return; }
    const lfrithTargets = state.playerField.filter(c => c && (c.name || '').includes('Gundam Lfrith'));
    if (lfrithTargets.length === 0) { onDone(); return; }
    const pilotCards = dofPilots.map(c => {
      const db = cardDb[c.cardNo];
      return db ? buildGameCardFromDb(db, c.copyVer) : { ...c };
    });
    showCardPickerModal(
      card.name,
      'You may pair a (Dawn of Fold) Command Pilot from your trash with a Gundam Lfrith unit.',
      pilotCards,
      (chosenPilot) => {
        if (!chosenPilot) { onDone(); return; }
        showCardPickerModal(
          card.name,
          `Choose a Gundam Lfrith unit to pair ${chosenPilot.name} with.`,
          lfrithTargets,
          (targetUnit) => {
            if (!targetUnit) { onDone(); return; }
            const slot = state.playerField.findIndex(c => c && c.uid === targetUnit.uid);
            if (slot === -1) { onDone(); return; }
            const trashIdx = state.playerTrash.findIndex(c =>
              c.cardNo === chosenPilot.cardNo && c.copyVer === chosenPilot.copyVer
            );
            if (trashIdx !== -1) {
              state.playerTrash.splice(trashIdx, 1);
              state.trashCount = Math.max(0, state.trashCount - 1);
            }
            const pilotCard = buildGameCardFromDb(cardDb[chosenPilot.cardNo] || chosenPilot, chosenPilot.copyVer);
            const pairedUnit = { ...targetUnit, pilot: pilotCard };
            if (!targetUnit.pilot && isLinkUnit(pairedUnit)) {
              pairedUnit.linkedThisTurn = true;
              pairedUnit.tempCantAttack = false;
              if (state.playerBase) {
                window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, pairedUnit, slot);
              }
            }
            state.playerField[slot] = pairedUnit;
            addLog(`${card.name} — paired ${pilotCard.name} (from trash) with ${targetUnit.name}.`);
            triggerCardEffect(pilotCard, 'pair', { unit: pairedUnit, slot });
            triggerCardEffect(pairedUnit, 'pair', { unit: pairedUnit, slot });
            NET.send({ type: 'PLAY_CARD', slot, card: state.playerField[slot], counters: myCounters() });
            broadcastCounters(); render(); setupDropZones();
            onDone();
          },
          card, { mandatory: true }
        );
      },
      card, { mandatory: false, skipLabel: 'Skip' }
    );
  },
});

// ─── GD04-022 · Kikeroga (MS Mode) ───────────────────────────────────────────
// All your Unit tokens gain <Breach 1>.
// [During Link] All Units Lv.3 or lower (other than Unit tokens) are deployed
// rested.
// "All Units" has no "friendly" qualifier — it has to force a rest on BOTH
// players' newly-deployed Units, not just the controller's own. game.js's
// hand-play deploy path (PLAY_CARD's handler) already has two separate hooks
// for exactly this split: passiveForceNonTokenDeployRested (scanned against
// the SAME side as the card being deployed, and only once the caller has
// already confirmed this card is linked — covers Kikeroga forcing its OWN
// controller's new Units to rest) and forceEnemyDeployRestedThreshold
// (scanned against the OPPOSITE side, with no such pre-check — covers
// Kikeroga forcing the OPPONENT's new Units to rest). Only the first was
// registered before, so the "During Link" clause silently applied to just
// one side instead of "All Units". Neither hook ever even runs for a token
// deploy in the first place (see _placeTokenAtSlot — token deploys use a
// separate, simpler path that doesn't check either one), so "other than
// Unit tokens" needs no explicit exclusion on either side here.
registerCardEffect('GD04-022', {
  passiveGlobalTokenBreach: () => 1,
  passiveForceNonTokenDeployRested: (card, deployedCard) =>
    (parseInt(deployedCard.level) || 0) <= 3,
  forceEnemyDeployRestedThreshold: (card) => isLinkUnit(card) ? 3 : -1,
});

// ─── GD04-023 · Gundam Kyrios (Tail Booster) ─────────────────────────────────
// [Deploy] Choose 1 of your Units paired with a (Super Soldier) Pilot. During
// this turn, it may choose an active enemy Unit Lv.4 or lower as its attack target.
registerCardEffect('GD04-023', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c =>
      c && c.pilot && (c.pilot.trait || '').includes('(Super Soldier)')
    );
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'Choose 1 Unit paired with a (Super Soldier) Pilot. It may attack active enemy Units Lv.4 or lower this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveLv4')) target.tempKeywords.push('CanAttackActiveLv4');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveLv4' });
        addLog(`${card.name} — ${target.name} can attack active enemy Units Lv.4 or lower this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-024 · Gundam Aerial Rebuild ────────────────────────────────────────
// [Deploy] Look at the top 3 cards of your deck. You may reveal 1 (Academy)
// Unit card or Command card and add it to your hand.
registerCardEffect('GD04-024', {
  deploy: (card) => {
    lookAtTopAndPickToHand(
      card, 3,
      'Look at top 3 cards. Reveal 1 (Academy) Unit or Command card to add to your hand.',
      // (Academy) qualifies BOTH card kinds, not just Unit — previously any
      // Command card was allowed through regardless of trait.
      c => (c.type === 'unit' || c.type === 'unit-token' || c.type === 'command' || c.type === 'command pilot') &&
           (c.trait || '').includes('(Academy)')
    );
  },
});

// ─── GD04-025 · Gundvölva ────────────────────────────────────────────────────
// [Destroyed] During your turn, if you have another (Dawn of Fold) Unit in
// play, place 1 EX Resource.
registerCardEffect('GD04-025', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player' || !state.myTurn) return;
    const hasDoFUnit = state.playerField.some(c =>
      c && c.uid !== card.uid && (c.trait || '').includes('(Dawn of Fold)')
    );
    if (!hasDoFUnit) return;
    placeExResource(false);
    addLog(`${card.name} — [Destroyed] another (Dawn of Fold) Unit in play: placed 1 EX Resource.`);
  },
});

// ─── GD04-026 · Garma's Dopp ─────────────────────────────────────────────────
// [Deploy] Look at the top card of your deck. Return it to the top or place it
// in your trash.
registerCardEffect('GD04-026', {
  deploy: (card) => showTopOrTrashModal(card),
});

// ─── GD04-028 · Zakrello ─────────────────────────────────────────────────────
// [Attack] Choose 1 active enemy Unit. It gains <Blocker> during this turn.
registerCardEffect('GD04-028', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && !c.rested);
    if (candidates.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModal(
      card.name,
      '[Attack] Choose 1 active enemy Unit. It gains <Blocker> this turn.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('Blocker')) target.tempKeywords.push('Blocker');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Blocker' });
        addLog(`${card.name} — ${target.name} gains <Blocker> this turn.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD04-029 · Gundam Dynames (GN Full Shield) ──────────────────────────────
// [Once per Turn] If you have a (CB) Pilot in play, when this Unit receives
// damage from an enemy, reduce it by 1.
// passiveUnitBattleDamageReduction handles battle damage (side effect sets
// passiveUsedThisTurn). effectDamageReduction handles effect damage with the
// same OPT gate. Both run on both clients independently from the same state.
registerCardEffect('GD04-029', {
  passiveUnitBattleDamageReduction: (card) => {
    if (card.passiveUsedThisTurn) return 0;
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const hasCBPilot = state[`${ownerSide}Field`].some(c => c?.pilot && (c.pilot.trait || '').includes('(CB)'));
    if (!hasCBPilot) return 0;
    card.passiveUsedThisTurn = true;
    return 1;
  },
  effectDamageReduction: (card) => {
    if (card.passiveUsedThisTurn) return 0;
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const hasCBPilot = state[`${ownerSide}Field`].some(c => c?.pilot && (c.pilot.trait || '').includes('(CB)'));
    if (!hasCBPilot) return 0;
    card.passiveUsedThisTurn = true;
    return 1;
  },
});

// ─── GD04-030 · Chuchu's Demi Trainer ────────────────────────────────────────
// [Attack] Choose 1 of your other (Academy) Units. During this turn, it may
// choose an active enemy Unit Lv.3 or lower as its attack target.
registerCardEffect('GD04-030', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const unit = ctx.attacker;
    const candidates = state.playerField.filter(c =>
      c && c.uid !== unit.uid && (c.trait || '').includes('(Academy)')
    );
    if (candidates.length === 0) { ctx?.onDone?.(); return; }
    showCardPickerModal(
      card.name,
      '[Attack] Choose 1 other (Academy) Unit. It may attack active enemy Units Lv.3 or lower this turn.',
      candidates,
      (target) => {
        if (!target) { ctx?.onDone?.(); return; }
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveLv3')) target.tempKeywords.push('CanAttackActiveLv3');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveLv3' });
        addLog(`${card.name} — ${target.name} can attack active enemy Units Lv.3 or lower this turn.`);
        render();
        setupDropZones();
        ctx?.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD05 GREEN CARDS
// ════════════════════════════════════════════════════════════════════════════

// ─── GD05-017 · Nu Gundam ────────────────────────────────────────────────
// Breach 5 (native).
// [When Paired] You may choose 3 (Londo Bell) cards from your trash. Exile
// them from the game. If you do, choose 1 enemy Unit. Begin a battle between
// this Unit and it and only perform the damage step.
// skipToDamage reuses the shared dispatch('ATTACK', ...) shortcut (see
// registerAction('ATTACK', ...) in game.js) — Attack/Action/Block Steps never
// run at all, so cards gated on those steps can't activate during it. That
// same shortcut never rests the attacker for a skipToDamage battle (this
// bonus battle isn't a normal declared attack, and the card text never says
// to rest it), so this Unit keeps its real attack for the turn — no
// canStayActiveAfterAttack hook needed here.
registerCardEffect('GD05-017', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit) return;
    const candidates = state.playerTrash.filter(c => (c.trait || '').includes('(Londo Bell)'));
    if (candidates.length < 3) {
      addLog(`${card.name} — fewer than 3 (Londo Bell) cards in trash. No effect.`);
      return;
    }
    showCardPickerModalMulti(
      card.name,
      'You may exile 3 (Londo Bell) cards from your trash. If you do, begin a battle (damage step only) against 1 enemy Unit.',
      candidates,
      { min: 3, max: 3, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 3) {
          addLog(`${card.name} — declined. No effect.`);
          return;
        }
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        const enemyTargets = state.oppField.filter(c => c);
        broadcastCounters(); render(); setupDropZones();
        if (enemyTargets.length === 0) {
          addLog(`${card.name} — no enemy Units to target. No further effect.`);
          return;
        }
        showCardPickerModal(
          card.name,
          'Choose 1 enemy Unit. Begin a battle (damage step only) against it.',
          enemyTargets,
          (target) => {
            if (!target) return;
            dispatch('ATTACK', {
              attackerUid: unit.uid,
              defenderUid: target.uid,
              isShield: false,
              isDirect: false,
              skipToDamage: true,
            });
          },
          card,
          { mandatory: true }
        );
      },
      card
    );
  },
});

// ─── GD05-018 · Gundam Calibarn ────────────────────────────────────────────
// [Deploy] Place 3 EX Resources.
// When one of your EX Resources is spent, you may choose 1 of your Units.
// During this turn, when it receives enemy damage, reduce it by 3.
registerCardEffect('GD05-018', {
  deploy: (card) => {
    placeExResource(false);
    placeExResource(false);
    placeExResource(false);
    addLog(`${card.name} — [Deploy] placed 3 EX Resources.`);
  },
  onExResourceSpent: (card) => {
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(
      card.name,
      'EX Resource spent: you may choose 1 of your Units. Reduce enemy damage to it by 3 this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempIncomingDamageReduction = (target.tempIncomingDamageReduction || 0) + 3;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 3, field: 'tempIncomingDamageReduction' });
        addLog(`${card.name} — ${target.name} will reduce enemy damage by 3 this turn.`);
        render(); setupDropZones();
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD05-019 · Re-GZ ────────────────────────────────────────────────────
// [Destroyed] Look at the top 3 cards of your deck. You may reveal 1 (Londo
// Bell) Unit card among them and add it to your hand. Return the remaining
// cards randomly to the bottom of your deck.
registerCardEffect('GD05-019', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    lookAtTopAndPickToHand(
      card, 3,
      'Look at top 3 cards. Reveal 1 (Londo Bell) Unit card to add to your hand.',
      c => (c.type === 'unit' || c.type === 'unit-token') && (c.trait || '').includes('(Londo Bell)')
    );
  },
});

// ─── GD05-020 · Nu Gundam ────────────────────────────────────────────────
// [During Pair] This Unit gains <Breach 3>.
// [Deploy] If there are 2 or more (Londo Bell) cards in your trash, place 1
// EX Resource.
registerCardEffect('GD05-020', {
  conditionalBreachValue: (card) => card.pilot ? 3 : 0,
  deploy: (card) => {
    const londoBellCount = state.playerTrash.filter(c => (c.trait || '').includes('(Londo Bell)')).length;
    if (londoBellCount < 2) return;
    placeExResource(false);
    addLog(`${card.name} — [Deploy] 2+ (Londo Bell) cards in trash: placed 1 EX Resource.`);
  },
});

// ─── GD05-021 · Gundam AGE-2 Double Bullet ─────────────────────────────────
// [Activate･Action][Once per Turn]①: This Unit gets AP+4 during this battle.
// [Once per Turn] When this Unit receives enemy damage, if you have an
// (Earth Federation) Pilot in play, reduce it by 2.
// activateActionUsedThisTurn gate + RES cost are handled generically by
// resolveUnitActionActivate/getActionEligibleUnits (see GD01-082 for the
// same pattern). The damage-reduction OPT gate reuses passiveUsedThisTurn.
registerCardEffect('GD05-021', {
  action: (card, ctx) => {
    if ((state.res + state.exRes) < 1) {
      addLog(`${card.name} — need ① RES to activate (have ${state.res + state.exRes}).`);
      ctx.onDone();
      return true;
    }
    spendResources(1);
    broadcastCounters();
    applyApMod([card], 4, 'battleApMod');
    addLog(`${card.name} — [Activate･Action] gets AP+4 during this battle.`);
    render();
    ctx.onDone();
    return true;
  },
  passiveUnitBattleDamageReduction: (card) => {
    if (card.passiveUsedThisTurn) return 0;
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const hasEFPilot = state[`${ownerSide}Field`].some(c => c?.pilot && (c.pilot.trait || '').includes('(Earth Federation)'));
    if (!hasEFPilot) return 0;
    card.passiveUsedThisTurn = true;
    return 2;
  },
  effectDamageReduction: (card) => {
    if (card.passiveUsedThisTurn) return 0;
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const hasEFPilot = state[`${ownerSide}Field`].some(c => c?.pilot && (c.pilot.trait || '').includes('(Earth Federation)'));
    if (!hasEFPilot) return 0;
    card.passiveUsedThisTurn = true;
    return 2;
  },
});

// ─── GD05-022 · Gundam Schwarzette ─────────────────────────────────────────
// Breach 3 (native).
// [Activate･Action] Exile 2 Command cards in your trash from the game:
// During this battle, when this Unit receives enemy damage (battle or
// effect), reduce it by 2.
// Cost has no RES component (getUnitActivationCost's res gate is trivially
// satisfied at 0) — the Command-card exile cost is checked/paid by hand,
// mirroring EB01-001's Activate･Main exile-cost pattern but through the
// action/canAction hooks since this is Activate･Action. battleDefMod is
// captured then cleared once per battle at the top of runDamageStep, and is
// honored for both battle damage (runDamageStep) and effect damage received
// during that same still-open battle (dealDamageToUnit).
registerCardEffect('GD05-022', {
  canAction: (card) => {
    return state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot').length >= 2;
  },
  action: (card, ctx) => {
    const cmdCandidates = state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot');
    if (cmdCandidates.length < 2) {
      addLog(`${card.name} — need 2 Command cards in trash to activate. No effect.`);
      ctx.onDone();
      return true;
    }
    showCardPickerModalMulti(
      card.name,
      'Exile 2 Command cards from your trash. During this battle, reduce enemy damage to this Unit by 2.',
      cmdCandidates,
      { min: 2, max: 2, mandatory: true },
      (chosen) => {
        if (!chosen || chosen.length < 2) { ctx.onDone(); return; }
        exileCardsFromTrash(chosen);
        card.battleDefMod = (card.battleDefMod || 0) + 2;
        NET.send({ type: 'FIELD_AP_MOD', uids: [card.uid], delta: 2, field: 'battleDefMod' });
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}. Reduces incoming damage by 2 this battle.`);
        broadcastCounters();
        render();
        ctx.onDone();
      },
      card
    );
    return true;
  },
});

// ─── GD05-023 · Re-GZ BWS ────────────────────────────────────────────────
// [Deploy] Place 1 EX Resource.
registerCardEffect('GD05-023', {
  deploy: (card) => {
    placeExResource(false);
    addLog(`${card.name} — [Deploy] placed 1 EX Resource.`);
  },
});

// ─── GD05-024 · Gundam AGE 2 Normal (SP Ver.) ──────────────────────────────
// [Destroyed] Choose 1 green (Earth Federation) Pilot card from your trash.
// Add it to your hand. If you do, discard 1.
registerCardEffect('GD05-024', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    const candidates = state.playerTrash.filter(c =>
      c.type === 'pilot' && (c.color || '').toLowerCase() === 'green' && (c.trait || '').includes('(Earth Federation)')
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no green (Earth Federation) Pilot cards in trash. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 green (Earth Federation) Pilot card from your trash to add to your hand.',
      candidates,
      (target) => {
        if (!target) return;
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render();
        if (state.hand.length === 0) { setupDropZones(); return; }
        showCardPickerModal(card.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
          if (discard) {
            state.hand = state.hand.filter(c => c.uid !== discard.uid);
            updateTempState(discard.cardNo, discard.copyVer, 'trash');
            state.trashCount++;
            state.playerTrash.push(toTrashCard(discard));
            broadcastLog(`${card.name} — discarded ${discard.name}.`);
            broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
          }
          broadcastCounters(); render(); setupDropZones();
        }, card, { mandatory: true });
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD05-025 · Demi Barding ──────────────────────────────────────────────
// [Deploy] Look at the top 3 cards of your deck. You may reveal 1 Command
// card among them and add it to your hand.
registerCardEffect('GD05-025', {
  deploy: (card) => {
    lookAtTopAndPickToHand(
      card, 3,
      'Look at top 3 cards. Reveal 1 Command card to add to your hand.',
      c => c.type === 'command' || c.type === 'command pilot'
    );
  },
});

// ─── GD05-026 · Gundam Aerial Rebuild ──────────────────────────────────────
// Count up the number of your Units with "Gundam Lfrith"/"Gundnode" in their
// card name, plus this Unit. All enemy Units whose Lv. is equal to or lower
// than that number are deployed rested.
// forceEnemyDeployRestedThreshold is checked generically in PLAY_CARD (game.js)
// against state.oppField from the DEPLOYING player's perspective.
registerCardEffect('GD05-026', {
  forceEnemyDeployRestedThreshold: (card) => {
    const ownerSide = state.playerField.some(c => c && c.uid === card.uid) ? 'player' : 'opp';
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const matching = field.filter(c =>
      c && c.uid !== card.uid && ((c.name || '').includes('Gundam Lfrith') || (c.name || '').includes('Gundnode'))
    ).length;
    return matching + 1; // "plus this Unit"
  },
});

// ─── GD05-028 · Kayra's Jegan ────────────────────────────────────────────
// [Deploy] Choose 1 of your (Londo Bell) Units. During this turn, it may
// choose an active enemy Unit with 4 or less AP as its attack target.
registerCardEffect('GD05-028', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Londo Bell)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Londo Bell) Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Londo Bell) Units. It may attack active enemy Units with 4 or less AP this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveAP4')) target.tempKeywords.push('CanAttackActiveAP4');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP4' });
        addLog(`${card.name} — ${target.name} can attack active enemy Units with 4 or less AP this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD05-029 · Kayra's Re-GZ ────────────────────────────────────────────
// [Deploy] Look at the top card of your deck. Return it to the top or bottom
// of your deck.
registerCardEffect('GD05-029', {
  deploy: (card) => showTopOrBottomModal(card),
});

// ─── GD05-030 · Michaelis ────────────────────────────────────────────────
// On the turn this Unit is deployed, it may choose a rested enemy Unit as
// its attack target and attack it. It cannot attack a base, shield, or the
// player directly until the next turn.
// tempAttackDespiteSickness overrides the deploy-sickness lock in
// cardCantAttackNow; tempCantAttackNonUnit restricts targeting to Units
// only. Both are self-only reads of the controller's own field (see
// registry.js), so no NET mirroring is needed.
registerCardEffect('GD05-030', {
  deploy: (card) => {
    card.tempAttackDespiteSickness = true;
    card.tempCantAttackNonUnit = true;
    addLog(`${card.name} — [Deploy] may attack a rested enemy Unit this turn despite just being deployed.`);
  },
});

// ─── GD05-085 · Amuro Ray — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// During your turn, when this Unit destroys an enemy Unit with battle
// damage, this Unit recovers 2 HP.
registerCardEffect('GD05-085', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    // deferredApplyHpMod, not applyHpMod directly — this fires before the
    // Unit's own counter-damage from this same exchange is applied (see
    // deferredApplyHpMod's comment in white.js), which would otherwise cap
    // the recovery to 0 whenever the Unit was undamaged going into the battle.
    deferredApplyHpMod([unit], 2);
    addLog(`${pilot.name} — ${unit.name} destroyed ${destroyedUnit.name} in battle. Recovers 2 HP.`);
  },
});

// ─── GD05-086 · Kayra Su — Pilot ────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] Enemy Units other than Link Units choose this rested Unit as
// their attack target if possible when attacking.
// pilotForcedAttackTarget is checked by getForcedAttackTargetUid(defenderSide,
// attacker) for every field card with a paired pilot (see registry.js).
registerCardEffect('GD05-086', {
  pilotForcedAttackTarget: (unitCard, pilotCard, ownerSide, attacker) => {
    if (!unitCard.rested) return false;
    if (!isLinkUnit(unitCard)) return false;
    if (attacker && isLinkUnit(attacker)) return false;
    return true;
  },
});

// ─── GD05-087 · Lauda Neill — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is (Academy), it gains <High-Maneuver>.
// pilotPassiveKeywords is checked live both for the UI badge (hasKwIcon) and
// for real gameplay (hasNativeKeyword) — see game.js.
registerCardEffect('GD05-087', {
  pilotPassiveKeywords: (pilot, unit) =>
    (unit.trait || '').includes('(Academy)') ? ['High-Maneuver'] : [],
});

// ─── GD05-088 · Prospera Mercury — Pilot ───────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// This Unit and all your Units with "Gundam Lfrith" or "Gundnode" in their
// card name get AP+1.
registerCardEffect('GD05-088', {
  globalPilotApBonus: (targetCard, pilot, sourceUnit, ownerSide) => {
    if (targetCard.uid === sourceUnit.uid) return 1;
    if ((targetCard.name || '').includes('Gundam Lfrith') || (targetCard.name || '').includes('Gundnode')) return 1;
    return 0;
  },
});

// ─── GD05-106 · Mutual Attraction — Command ────────────────────────────────
// [Main] When playing this card, choose 1 of the following effects and
// activate it:
// ■ Place 1 rested Resource.
// ■ Choose 1 Pilot card that is Lv.5 or higher from your trash. Add it to
//   your hand.
function gd05106Effect(card, onDone) {
  showChooseOneEffectModal(card, [
    {
      label: 'Place 1 rested Resource.',
      run: (done) => {
        state.resBase = Math.min(state.resBase + 1, 10);
        addLog(`${card.name} — placed 1 rested Resource (level now: ${state.resBase + state.exRes + state.exResRested}).`);
        broadcastCounters();
        render();
        done();
      },
    },
    {
      label: 'Choose 1 Pilot card that is Lv.5 or higher from your trash. Add it to your hand.',
      run: (done) => {
        const candidates = state.playerTrash.filter(c => c.type === 'pilot' && (parseInt(c.level) || 0) >= 5);
        if (candidates.length === 0) {
          addLog(`${card.name} — no Lv.5+ Pilot cards in trash. No effect.`);
          done();
          return;
        }
        showCardPickerModal(card.name, 'Choose 1 Pilot card that is Lv.5 or higher from your trash.', candidates, (target) => {
          if (target) {
            const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
            if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
            updateTempState(target.cardNo, target.copyVer, 'hand');
            state.hand.push(target);
            addLog(`${card.name} — recovered ${target.name} from trash to hand.`);
            broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
            glowHandCard(target.uid);
            broadcastCounters(); render();
          }
          done();
        }, card, { mandatory: true });
      },
    },
  ], onDone);
}
registerCardEffect('GD05-106', {
  main: (card, ctx) => gd05106Effect(card, ctx.onDone),
});

// ─── GD05-107 · Interwoven Blessings — Command ─────────────────────────────
// [Burst] Place 1 EX Resource.
// [Main] Choose 1 enemy player. Destroy the first 2 cards in that player's
// shield area (simultaneously, like native Suppression).
// Only the shield owner's own client has the authoritative private shield
// identities, so FORCE_SHIELD_HIT just tells them to resolve 2 of their own
// shields the same way resolveMyShieldHit() already does for combat — see
// network.js.
registerCardEffect('GD05-107', {
  burst: (card) => {
    placeExResource();
    addLog(`${card.name} — [Burst] placed 1 EX Resource.`);
  },
  main: (card, ctx) => {
    NET.send({ type: 'FORCE_SHIELD_HIT', count: 2 });
    addLog(`${card.name} — [Main] destroyed the first 2 cards in the opponent's shield area.`);
    ctx.onDone?.();
  },
});

// ─── GD05-108 · Overcoming Hardships — Command Pilot ───────────────────────
// [Action] Choose 1 friendly rested (Academy) Unit. Change a battling enemy
// Unit's attack target to it.
// [Pilot] Guel Jeturk
// Same "redirect a battling enemy Unit's attack target" mechanic as ST07-013
// Armed Intervention, just (Academy) instead of (CB).
function gd05108AcademyCandidates() {
  return state.playerField.filter(c => c && c.rested && (c.trait || '').includes('(Academy)'));
}
registerCardEffect('GD05-108', {
  canActivate: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'opp') return false;
    return gd05108AcademyCandidates().length > 0;
  },
  action: (card, ctx) => {
    const candidates = gd05108AcademyCandidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested friendly (Academy) Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 rested friendly (Academy) Unit. The enemy attack is redirected to it.', candidates, (target) => {
      if (target) {
        ctx.battle.defender = target;
        ctx.battle.isShield = false;
        ctx.battle.isDirect = false;
        NET.send({ type: 'CHANGE_ATTACK_TARGET', uid: target.uid });
        addLog(`${card.name} — attack redirected to ${target.name}.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-109 · Felsi's Plea — Command Pilot ───────────────────────────────
// [Action] Choose 1 friendly (Academy) Unit. It recovers 2 HP. Then, if it is
// paired with a Pilot that is Lv.3 or lower, draw 1.
// [Pilot] Felsi Rollo
function gd05109Candidates() {
  return state.playerField.filter(c => c && (c.trait || '').includes('(Academy)'));
}
registerCardEffect('GD05-109', {
  canActivate: () => gd05109Candidates().length > 0,
  action: (card, ctx) => {
    const candidates = gd05109Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Academy) Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 friendly (Academy) Unit. It recovers 2 HP.', candidates, (target) => {
      if (target) {
        applyHpMod([target], 2);
        addLog(`${card.name} — ${target.name} recovers 2 HP.`);
        if (target.pilot && (parseInt(target.pilot.level) || 0) <= 3) {
          const drawn = drawCardsFromTempDeck(1, true);
          if (drawn.length > 0) {
            state.hand.push(drawn[0]);
            addLog(`${card.name} — ${target.name} paired with a Lv.3 or lower Pilot: drew ${drawn[0].name}.`);
          }
        }
        broadcastCounters(); render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-125 · Ra Cailum — Base ───────────────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Activate･Main] Rest this Base: Choose 1 friendly (Londo Bell) Unit.
// During this turn, when it receives enemy damage, reduce it by 1.
// "Rest this Base" cost is parsed generically by getBaseActivationCost().
registerCardEffect('GD05-125', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(Londo Bell)')),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Londo Bell)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Londo Bell) Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 friendly (Londo Bell) Unit. Reduce enemy damage to it by 1 this turn.', candidates, (target) => {
      if (target) {
        target.tempIncomingDamageReduction = (target.tempIncomingDamageReduction || 0) + 1;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'tempIncomingDamageReduction' });
        addLog(`${card.name} — ${target.name} will reduce enemy damage by 1 this turn.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-126 · Quiet Zero — Base ──────────────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Activate･Main][Once per Turn]②: If you have a Unit with "Gundam Aerial" in
// its card name that is Lv.5 or higher in play, deploy 1 [Gundnode]
// ((Quiet Zero)･AP2･HP2･<Breach 1>) Unit token.
// RES cost + Once-per-Turn gate parsed generically by getBaseActivationCost().
function gd05126Qualifies() {
  return state.playerField.some(c => c && (c.name || '').includes('Gundam Aerial') && (parseInt(c.level) || 0) >= 5);
}
registerCardEffect('GD05-126', {
  canActivate: () => gd05126Qualifies(),
  activate: (card, ctx) => {
    if (!gd05126Qualifies()) {
      addLog(`${card.name} — no Lv.5+ Unit with "Gundam Aerial" in its name in play. No effect.`);
      ctx.onDone();
      return;
    }
    deployTokenToField('T-026', {});
    addLog(`${card.name} — deployed 1 Gundnode Unit token.`);
    ctx.onDone();
  },
});

// ─── ST13-011 · Haman Karn — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Look at the top 5 cards of your deck. You may reveal 1 Pilot
// card whose Lv. is equal to or lower than this Unit among them and add it
// to your hand. Return the remaining cards randomly to the bottom of your
// deck. "this Unit" = the Unit Haman was just paired to, not Haman's own Lv.
registerCardEffect('ST13-011', {
  pair: (pilot, ctx) => {
    const unitLevel = parseInt(ctx?.unit?.level) || 0;
    lookAtTopAndPickToHand(
      pilot, 5,
      `Look at top 5 cards. Reveal 1 Pilot card Lv.${unitLevel} or lower to add to your hand.`,
      c => (c.type === 'pilot' || c.type === 'command pilot') && (parseInt(c.level) || 0) <= unitLevel,
      ctx?.onDone
    );
  },
});

// ─── T-029 · Bit / Funnel — Unit Token ──────────────────────────────────────
// "This Unit can't be paired with a Pilot or attack." Shared by every card
// below that deploys this token (ST13-001 Qubeley, ST13-002 Elmeth, ST13-013
// I'm a Newtype, ST13-016 Sodon) — cardNo 'T-029' is a real database entry
// (assets/nuCardDatabase.json), not a synthetic/placeholder one, so
// deployTokenToField('T-029') resolves it directly with no extra setup.
registerCardEffect('T-029', {
  cantAttack: () => true,
  // Qubeley's own [Activate･Main] begins a battle FOR the token instead of
  // it declaring a real attack — the shared skipToDamage dispatch shortcut
  // (see registerAction('ATTACK', ...) in game.js) never rests the attacker
  // for that kind of battle, so the token needs no hook of its own here.
});

// ─── ST13-002 · Elmeth ──────────────────────────────────────────────────────
// [Deploy] Deploy 1 [Bit / Funnel]((Long-Range Weapon)·AP2·HP2·This Unit
// can't be paired with a Pilot or attack) Unit token.
registerCardEffect('ST13-002', {
  deploy: (card) => {
    const token = deployTokenToField('T-029');
    if (token) {
      addLog(`${card.name} — deployed ${token.name} token.`);
    } else {
      addLog(`${card.name} — no empty field slot for Bit / Funnel token.`);
    }
    render();
    setupDropZones();
  },
});

// ─── ST13-001 · Qubeley ──────────────────────────────────────────────────────
// [Once per Turn] During your turn, when you pair a Pilot with one of your
// Units (including Qubeley itself), deploy 1 to 2 [Bit / Funnel] Unit tokens
// (Lv.0･AP2･HP2, can't be paired with a Pilot or attack) — 2 if 2+ field
// slots are open when it triggers, 1 if only 1 slot is open, 0 (no effect)
// if the field is full.
// [Activate･Main][Once per Turn] ①: Choose 1 of your Unit tokens and 1 enemy
// Unit. Begin a battle between them and only perform the damage step.
// RES cost + Once-per-Turn gate for the Activate·Main ability are parsed
// generically from the printed text by getUnitActivationCost().
function qubeleyDeployBitFunnelTokens(card) {
  if (!state.myTurn || card.passiveUsedThisTurn) return;
  const emptySlots = state.playerField.filter(c => !c).length;
  if (emptySlots === 0) return;
  card.passiveUsedThisTurn = true;
  const count = emptySlots >= 2 ? 2 : 1;
  for (let i = 0; i < count; i++) {
    if (!state.playerField.some(c => !c)) break;
    deployTokenToField('T-029', { rested: false });
  }
  addLog(`${card.name} — deployed ${count} [Bit / Funnel] Unit token${count > 1 ? 's' : ''}.`);
}

registerCardEffect('ST13-001', {
  // Fires when Qubeley itself is the one just paired...
  pair: (card) => qubeleyDeployBitFunnelTokens(card),
  // ...and when any OTHER friendly Unit is paired (see PAIR_PILOT in game.js,
  // "Notify other field units that a ... unit was just paired").
  onAnyUnitPair: (card) => qubeleyDeployBitFunnelTokens(card),
  canActivate: () =>
    state.playerField.some(c => c && isUnitToken(c)) &&
    state.oppField.some(c => c),
  activate: (card, ctx) => {
    const tokens = state.playerField.filter(c => c && isUnitToken(c));
    const enemies = state.oppField.filter(c => c);
    if (tokens.length === 0 || enemies.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 of your Unit tokens.', tokens, (token) => {
      if (!token) { ctx.onDone(); return; }
      showCardPickerModal(card.name, 'Choose 1 enemy Unit. Begin a battle (damage step only) against it.', enemies, (target) => {
        if (!target) { ctx.onDone(); return; }
        dispatch('ATTACK', {
          attackerUid: token.uid,
          defenderUid: target.uid,
          isShield: false,
          isDirect: false,
          skipToDamage: true,
        });
        ctx.onDone();
      }, card, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// ─── ST14-015 · Battlefield Emotions ────────────────────────────────────────
// [Burst] Place 1 EX Resource.
// [Main] Place 1 rested Resource. Then, if you have not set one of your
// Resources as active with an effect this turn, choose a number of your
// Resources equal to the number of enemy players. Set them as active.
// This app is always a 1v1 match, so "the number of enemy players" is always
// 1 — same assumption every other single-opponent-referencing effect in this
// codebase already makes. state.resourceSetActiveThisTurn (game.js) is the
// shared "did any effect set a Resource active this turn" flag, checked
// BEFORE this card's own placement below sets it.
registerCardEffect('ST14-015', {
  burst: (card) => {
    placeExResource();
    addLog(`${card.name} — [Burst] placed 1 EX Resource.`);
  },
  main: (card, ctx) => {
    const alreadySetActive = state.resourceSetActiveThisTurn;
    // A rested resource counts toward level (resBase++) but is not spendable this turn.
    state.resBase = Math.min(state.resBase + 1, 10);
    addLog(`${card.name} — placed 1 rested Resource (level now: ${state.resBase + state.exRes + state.exResRested}).`);
    if (!alreadySetActive) {
      if (state.res < state.resBase) {
        state.res = Math.min(state.res + 1, state.resBase);
        state.resourceSetActiveThisTurn = true;
        addLog(`${card.name} — no Resource set active by an effect yet this turn: set 1 Resource as active (RES now ${state.res}).`);
      } else {
        addLog(`${card.name} — all Resources already active. No further effect.`);
      }
    } else {
      addLog(`${card.name} — already set a Resource active with an effect this turn. No further effect.`);
    }
    broadcastCounters();
    render();
    ctx.onDone?.();
  },
});

// ─── ST13-013 · I'm a Newtype — Command Pilot ───────────────────────────────
// [Main] Deploy 1 to 2 [Bit / Funnel]((Long-Range Weapon)･AP2･HP2･This Unit
// can't be paired with a Pilot or attack) Unit token.
// Same "as many as fit, up to 2" resolution as ST13-001 Qubeley's identical
// token-count wording — no printed condition ties the count to anything else.
registerCardEffect('ST13-013', {
  main: (card, ctx) => {
    const emptySlots = state.playerField.filter(c => !c).length;
    if (emptySlots === 0) {
      addLog(`${card.name} — no empty field slot for Bit / Funnel token(s).`);
      ctx.onDone();
      return;
    }
    const count = emptySlots >= 2 ? 2 : 1;
    for (let i = 0; i < count; i++) {
      if (!state.playerField.some(c => !c)) break;
      deployTokenToField('T-029', { rested: false });
    }
    addLog(`${card.name} — deployed ${count} [Bit / Funnel] Unit token${count > 1 ? 's' : ''}.`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ─── ST13-016 · Sodon — Base ─────────────────────────────────────────────────
// [Burst] Deploy this card. [Deploy] Add 1 of your Shields to your hand —
// both handled generically by deployBaseFromShield()/applyBaseDeployEffect()
// in js/game.js (see ST11-016's identical comment). Then, deploy 1
// [Bit / Funnel]((Long-Range Weapon)･AP2･HP2･This Unit can't be paired with
// a Pilot or attack) Unit token.
registerCardEffect('ST13-016', {
  deploy: (card) => {
    const token = deployTokenToField('T-029');
    if (token) {
      addLog(`${card.name} — deployed ${token.name} token.`);
    } else {
      addLog(`${card.name} — no empty field slot for Bit / Funnel token.`);
    }
    render();
    setupDropZones();
  },
});

// ─── ST13-005 · GFreD ────────────────────────────────────────────────────────
// [Deploy] If 4 or more enemy Units are in play, look at the top 5 cards of
// your deck. You may reveal 1 Pilot card among them and add it to your hand.
// Return the remaining cards randomly to the bottom of your deck.
// "Pilot card" only — a Command Pilot card (categoryData "command pilot")
// does not qualify (unlike GD04-105 Encounter's identical-shaped effect,
// which accepts either pilot type).
registerCardEffect('ST13-005', {
  deploy: (card) => {
    if (state.oppField.filter(c => c).length < 4) return;
    lookAtTopAndPickToHand(
      card, 5,
      'You may reveal 1 Pilot card and add it to your hand.',
      c => c.type === 'pilot',
      null
    );
  },
});

// ─── ST14-006 · Full Armor Unicorn Gundam (Destroy Mode) ─────────────────────
// [Deploy] Look at the top 3 cards of your deck. You may reveal any number of
// them (1–3) and add them to your hand. Return the remaining cards randomly
// to the bottom of your deck.
// [During Pair][Once per Turn] When this Unit would receive battle damage
// from an enemy Unit with AP equal to or less than it, it doesn't receive
// that damage. (Effect damage is unaffected — see passiveUnitBattleDamageImmune's
// other users, e.g. GD03-020 Zaku II FZ, for the same battle-damage-only scope.)
registerCardEffect('ST14-006', {
  deploy: (card) => {
    const peeked = peekTopOfDeck(3);
    if (peeked.length === 0) {
      addLog(`${card.name} — deck is empty. No effect.`);
      return;
    }
    const shuffleToBottom = (entries) => {
      for (let i = entries.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [entries[i], entries[j]] = [entries[j], entries[i]];
      }
      entries.forEach(e => sendDeckEntryToBottom(e));
    };
    showCardPickerModalMulti(
      card.name,
      'Look at the top 3 cards of your deck. You may reveal any number of them and add them to your hand.',
      peeked.map(p => p.card),
      { min: 1, max: 3, mandatory: false },
      (chosen) => {
        const chosenUids = new Set((chosen || []).map(c => c.uid));
        const chosenPeeks = peeked.filter(p => chosenUids.has(p.card.uid));
        chosenPeeks.forEach(p => {
          p.entry.state = 'hand';
          state.hand.push(p.card);
          NET.send({ type: 'CARD_REVEAL', card: p.card, pilotName: card.name });
        });
        if (chosenPeeks.length > 0) {
          state.deckCount = state.tempDeck.filter(isInDeck).length;
          addLog(`${card.name} — revealed ${chosenPeeks.map(p => p.card.name).join(', ')} and added to hand.`);
          broadcastCardToast(`${chosenPeeks.length} card(s) added to hand due to ${card.name} effect.`);
        }
        const remaining = peeked.filter(p => !chosenUids.has(p.card.uid));
        shuffleToBottom(remaining.map(p => p.entry));
        broadcastCounters();
        render();
        setupDropZones();
      },
      card
    );
  },
  passiveUnitBattleDamageImmune: (card, attacker) => {
    if (!card.pilot) return false; // [During Pair] — must currently have a paired Pilot
    if (card.passiveUsedThisTurn) return false; // [Once per Turn]
    if (effectiveBp(attacker) > effectiveBp(card)) return false;
    card.passiveUsedThisTurn = true;
    return true;
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 batch — "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── GD01-092 · M'Quve — Pilot ─────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// While this Unit is (Zeon), it gains <Breach 1>.
// CONFIRMED BUG (same class as GD06-081 Amate Yuzuriha): only
// pilotPassiveKeywords was registered, which drives the Breach icon/badge
// display but NOT the actual combat bonus damage — getBreachValue()
// (game.js) only reads pilotConditionalBreachValue for a pilot-granted
// Breach amount, so the linked Unit showed the Breach badge but never
// actually dealt the bonus shield/base damage. Same two-hook pairing as
// GD01-087 Sayla Mass's Repair grant: pilotConditionalBreachValue drives
// the real bonus, pilotPassiveKeywords drives the badge.
registerCardEffect('GD01-092', {
  pilotConditionalBreachValue: (pilot, unit) => (unit.trait || '').includes('(Zeon)') ? 1 : 0,
  pilotPassiveKeywords: (pilot, unit) => (unit.trait || '').includes('(Zeon)') ? ['Breach 1'] : [],
});

// ─── EB01-040 · Gundam Epyon — Unit ────────────────────────────────────────
// [Deploy] If there are 2 or more enemy players, choose 1 to 3 friendly
// Units. They gain <Breach 3> during this turn.
// This app is a strict 1v1 match, so enemyPlayerCount() is always 1 and this
// branch never fires — see enemyPlayerCount()'s declaration in game.js. The
// full picker logic is still implemented (rather than a bare no-op) for
// correctness/documentation and in case of future FFA support.
registerCardEffect('EB01-040', {
  deploy: (card) => {
    if (enemyPlayerCount() < 2) {
      addLog(`${card.name} — [Deploy] fewer than 2 enemy players (1v1 match). No effect.`);
      return;
    }
    const candidates = state.playerField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModalMulti(
      card.name,
      'Choose 1 to 3 friendly Units. They gain <Breach 3> during this turn.',
      candidates,
      { min: 1, max: 3, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length === 0) return;
        chosen.forEach(target => {
          target.tempKeywords = target.tempKeywords || [];
          if (!target.tempKeywords.includes('Breach 3')) target.tempKeywords.push('Breach 3');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Breach 3' });
        });
        addLog(`${card.name} — ${chosen.map(c => c.name).join(', ')} gain <Breach 3> during this turn.`);
        render();
        setupDropZones();
      },
      card
    );
  },
});

// ─── GD06-105 · Grace Under Pressure — Command ─────────────────────────────
// [Main] Deploy 1 [Gundam Calibarn]((Academy)･AP5･HP3･<High-Maneuver>) Unit
// token or 1 [Gundam Aerial Rebuild]((Quiet Zero)･AP3･HP5･<Breach 3>) Unit
// token.
// Neither token variant has a matching real-card DB entry (existing "Gundam
// Calibarn"/"Gundam Aerial Rebuild" cards all have different stat lines), so
// T-035/T-036 were added as new synthetic token DB entries — see
// assets/nuCardDatabase.json.
registerCardEffect('GD06-105', {
  main: (card, ctx) => {
    const optA = buildGameCardFromDb(cardDb['T-035'], 'gd06105-preview-a');
    const optB = buildGameCardFromDb(cardDb['T-036'], 'gd06105-preview-b');
    showCardPickerModal(
      card.name,
      'Deploy 1 [Gundam Calibarn] or 1 [Gundam Aerial Rebuild] Unit token.',
      [optA, optB],
      (chosen) => {
        if (chosen) {
          deployTokenToField(chosen.cardNo);
          addLog(`${card.name} — deployed a [${chosen.name}] Unit token.`);
          broadcastCounters(); render(); setupDropZones();
        }
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-021 · Turn X (Top) — Unit ─────────────────────────────────────────
// [Deploy] Choose 1 of your Unit tokens. Deploy 1 rested Unit token of the
// same kind.
registerCardEffect('GD06-021', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && isUnitToken(c));
    if (candidates.length === 0) {
      addLog(`${card.name} — [Deploy] no Unit tokens in play. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your Unit tokens. Deploy 1 rested Unit token of the same kind.',
      candidates,
      (chosen) => {
        if (chosen) {
          deployTokenToField(chosen.cardNo, { rested: true });
          addLog(`${card.name} — [Deploy] deployed 1 rested ${chosen.name} Unit token.`);
          render();
          setupDropZones();
        }
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-109 · God of War Ghingnham — Command ─────────────────────────────
// [Main] Deploy 1 [Turn X (Arms)]((Ghingnham Fleet)(Long-Range Weapon)･AP2･
// HP3) Unit token or 1 rested [Turn X (Legs)]((Ghingnham Fleet)(Long-Range
// Weapon)･AP3･HP2) Unit token.
// Same "preview the two token choices via showCardPickerModal" shape as
// GD06-105 Grace Under Pressure, deploying T-031/T-032 (see GD06-019 Turn X,
// which deploys the same pair).
registerCardEffect('GD06-109', {
  main: (card, ctx) => {
    const optArms = buildGameCardFromDb(cardDb['T-031'], 'gd06109-preview-arms');
    const optLegs = buildGameCardFromDb(cardDb['T-032'], 'gd06109-preview-legs');
    showCardPickerModal(
      card.name,
      'Deploy 1 [Turn X (Arms)] Unit token, or 1 rested [Turn X (Legs)] Unit token.',
      [optArms, optLegs],
      (chosen) => {
        if (chosen) {
          const rested = chosen.cardNo === 'T-032';
          deployTokenToField(chosen.cardNo, { rested });
          addLog(`${card.name} — deployed${rested ? ' 1 rested' : ' 1'} [${chosen.name}] Unit token.`);
          broadcastCounters(); render(); setupDropZones();
        }
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-022 · Merrybell's Bandit — Unit ──────────────────────────────────
// [When Linked] Choose 1 enemy Unit with 3 or less AP. Begin a battle between
// this Unit and it and only perform the damage step.
// Same skipToDamage ATTACK shortcut as GD05-017 Nu Gundam/ST13-001 Qubeley —
// the shared dispatch handler never rests the attacker for a skipToDamage
// battle, so this Unit needs no canStayActiveAfterAttack hook of its own.
registerCardEffect('GD06-022', {
  pair: (card, ctx) => {
    const unit = ctx?.unit;
    if (!unit || !isLinkUnit(unit)) return;
    const candidates = state.oppField.filter(c => c && effectiveBp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Linked] no enemy Units with 3 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      '[When Linked] Choose 1 enemy Unit with 3 or less AP. Begin a battle between this Unit and it and only perform the damage step.',
      candidates,
      (target) => {
        if (!target) return;
        dispatch('ATTACK', {
          attackerUid: unit.uid,
          defenderUid: target.uid,
          isShield: false,
          isDirect: false,
          skipToDamage: true,
        });
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-087 · Merrybell Gadget — Pilot ───────────────────────────────────
// [Attack] Choose 1 of your (Ghingnham Fleet) Units. During this turn, it may
// choose an active enemy Unit that is Lv.4 or lower as its attack target.
// CanAttackActiveLv4 tempKeyword — same convention as ST14-012 Banagher
// Links/GD02-123 Sodon's CanAttackActiveAP<N> grants.
registerCardEffect('GD06-087', {
  attack: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Ghingnham Fleet)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — [Attack] no friendly (Ghingnham Fleet) Units. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Ghingnham Fleet) Units. It may choose an active enemy Unit that is Lv.4 or lower as its attack target this turn.',
      candidates,
      (target) => {
        if (target) {
          target.tempKeywords = target.tempKeywords || [];
          if (!target.tempKeywords.includes('CanAttackActiveLv4')) target.tempKeywords.push('CanAttackActiveLv4');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveLv4' });
          addLog(`${card.name} — ${target.name} may attack active enemy Units Lv.4 or lower this turn.`);
          render();
          setupDropZones();
        }
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-124 · Aspite — Base ──────────────────────────────────────────────
// [Burst] Deploy this card. (generic)
// [Deploy] Add 1 of your Shields to your hand. (generic)
// [Activate･Main] Rest this Base: If you have 3 or more (Ghingnham
// Fleet)/(Long-Range Weapon) Units in play, all your (Ghingnham Fleet) Units
// get AP+1 during this turn.
function gd06124QualifyingCount() {
  return state.playerField.filter(c => c && ((c.trait || '').includes('(Ghingnham Fleet)') || (c.trait || '').includes('(Long-Range Weapon)'))).length;
}
registerCardEffect('GD06-124', {
  canActivate: () => gd06124QualifyingCount() >= 3,
  activate: (card, ctx) => {
    if (gd06124QualifyingCount() < 3) {
      addLog(`${card.name} — fewer than 3 (Ghingnham Fleet)/(Long-Range Weapon) Units in play. No effect.`);
      ctx.onDone();
      return;
    }
    const targets = state.playerField.filter(c => c && (c.trait || '').includes('(Ghingnham Fleet)'));
    if (targets.length === 0) {
      addLog(`${card.name} — no friendly (Ghingnham Fleet) Units to buff. No effect.`);
      ctx.onDone();
      return;
    }
    applyApMod(targets, 1, 'tempApMod');
    addLog(`${card.name} — all friendly (Ghingnham Fleet) Units get AP+1 during this turn.`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ════════════════════════════════════════════════════════════════════════════
// ST11-14 batch — newly added cards, "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── ST13-004 · GX-Bit — Unit ───────────────────────────────────────────────
// [Deploy] Look at the top card of your deck. Return it to the top or bottom
// of your deck. Same shared helper as GD01-039 Dopp above.
registerCardEffect('ST13-004', {
  deploy: (card) => showTopOrBottomModal(card),
});

// ─── ST14-009 · Duel Gundam (Assault Shroud) — Unit ────────────────────────
// [Destroyed] Place 1 EX Resource.
registerCardEffect('ST14-009', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    placeExResource();
    addLog(`${card.name} — [Destroyed] placed 1 EX Resource.`);
  },
});

// ─── GD06-085 · Gym Ghingnham — Pilot ───────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] All your (Ghingnham Fleet) Units get AP+1 during your
// opponent's turn.
// globalPilotApBonus is checked live via getGlobalUnitApBonus() in
// effectiveBp() for every unit paired with this pilot's cardNo. Gated on
// isLinkUnit (the [During Link] condition — plain pairing alone isn't
// enough) and on it currently being the OWNER's opponent's turn, using the
// same ownerSide-relative state.myTurn check as GD03-033 Providence Gundam.
registerCardEffect('GD06-085', {
  globalPilotApBonus: (targetCard, pilot, sourceUnit, ownerSide) => {
    if (!isLinkUnit(sourceUnit)) return 0;
    const isOwnersOwnTurn = ownerSide === 'player' ? state.myTurn : !state.myTurn;
    if (isOwnersOwnTurn) return 0;
    return (targetCard.trait || '').includes('(Ghingnham Fleet)') ? 1 : 0;
  },
});

// ─── GD06-019 · Turn X — Unit ────────────────────────────────────────────────
// Enemy Units choose one of your rested Unit tokens as their attack target if
// possible when attacking.
// [Attack] If you have no (Ghingnham Fleet) Unit tokens in play, deploy 1
// rested Turn X (Arms) token ((Ghingnham Fleet)(Long-Range Weapon)・AP2/HP3)
// and 1 rested Turn X (Legs) token ((Ghingnham Fleet)(Long-Range Weapon)・
// AP3/HP2) — see T-031/T-032 in the card database.
// forcedAttackTargetPool mirrors GD03-025 Gundam Sandrock Custom's pattern,
// filtering by isUnitToken() instead of a trait (this card's text says "Unit
// tokens", not any specific trait).
registerCardEffect('GD06-019', {
  forcedAttackTargetPool: (card, defenderSide) => {
    const field = state[`${defenderSide}Field`];
    return field.filter(c => c && c.rested && isUnitToken(c));
  },
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const hasGhingnhamToken = state.playerField.some(c => c && isUnitToken(c) && (c.trait || '').includes('(Ghingnham Fleet)'));
    if (hasGhingnhamToken) { ctx.onDone(); return; }
    addLog(`${card.name} — [Attack] no (Ghingnham Fleet) Unit tokens in play: deploying Turn X (Arms) and Turn X (Legs) tokens.`);
    deployTokenToField('T-031', { rested: true });
    deployTokenToField('T-032', { rested: true });
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// ─── GD06-026 · Stero's Mahiroo — Unit ──────────────────────────────────────
// [Attack] If you have 3 or more other (Ghingnham Fleet)/(Long-Range Weapon)
// Units in play, choose 1 of your Units. It gets AP+1 and gains <Repair 2>
// during this turn.
registerCardEffect('GD06-026', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const otherMatching = state.playerField.filter(c =>
      c && c.uid !== card.uid &&
      ((c.trait || '').includes('(Ghingnham Fleet)') || (c.trait || '').includes('(Long-Range Weapon)'))
    );
    if (otherMatching.length < 3) {
      addLog(`${card.name} — need 3+ other (Ghingnham Fleet)/(Long-Range Weapon) Units in play.`);
      ctx.onDone?.(); return;
    }
    const candidates = state.playerField.filter(c => c);
    showCardPickerModal(
      card.name,
      'Choose 1 of your Units. It gets AP+1 and gains <Repair 2> during this turn.',
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], 1, 'tempApMod');
          target.tempKeywords = target.tempKeywords || [];
          target.tempKeywords.push('Repair 2');
          NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'Repair 2' });
          addLog(`${card.name} — ${target.name} gets AP+1 and gains <Repair 2> this turn.`);
          render();
          setupDropZones();
        }
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-107 · Wheel Spoke Formation — Command Pilot ──────────────────────
// [Main] If you have a (Ghingnham Fleet) Unit in play, choose 1 enemy Unit
// with <Blocker> and 3 or less AP. Destroy it.
// [Pilot] [Sweatson Stero]
function gd06107Candidates() {
  return state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker') && effectiveBp(c) <= 3);
}
registerCardEffect('GD06-107', {
  canActivate: () =>
    state.playerField.some(c => c && (c.trait || '').includes('(Ghingnham Fleet)')) &&
    gd06107Candidates().length > 0,
  main: (card, ctx) => {
    if (!state.playerField.some(c => c && (c.trait || '').includes('(Ghingnham Fleet)'))) {
      addLog(`${card.name} — no friendly (Ghingnham Fleet) Units in play. No effect.`);
      ctx.onDone(); return;
    }
    const candidates = gd06107Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with <Blocker> and 3 or less AP. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with <Blocker> and 3 or less AP. Destroy it.',
      candidates,
      (target) => {
        if (target) effectDestroyEnemyUnit(target);
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-084 · Elpeo Ple — Pilot ────────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] During this turn, this Unit gains the same number of
// <Breach 1> as the number of (Neo Zeon)/(Long-Range Weapon) Units you have
// in play.
// Live-evaluated via pilotConditionalBreachValue instead of a one-time
// tempKeyword snapshot pushed at pairing time — CONFIRMED BUG found via live
// play: a one-time push froze the Breach value at whatever the qualifying-
// unit count was the instant this pilot paired, so deploying more (Long-
// Range Weapon) Unit tokens later the same turn (e.g. GD06-019 Turn X's own
// Turn X (Arms)/(Legs) tokens — any friendly Unit token with the trait
// qualifies, same as a printed Unit) never raised it: attacking with 3
// qualifying Units in play still only dealt 1 Breach damage if only 1 was in
// play back when the pairing happened. getBreachValue() (game.js) calls this
// hook every time Breach damage is actually dealt, so the value always
// matches the field right now, including 0 (no Breach at all) when no
// qualifying Unit is currently in play.
registerCardEffect('GD06-084', {
  pilotConditionalBreachValue: (pilot, unit) => {
    if (!unit) return 0;
    const isPlayerSide = state.playerField.some(c => c && c.uid === unit.uid);
    const isOppSide    = !isPlayerSide && state.oppField.some(c => c && c.uid === unit.uid);
    if (!isPlayerSide && !isOppSide) return 0;
    const field = isPlayerSide ? state.playerField : state.oppField;
    return field.filter(c =>
      c && ((c.trait || '').includes('(Neo Zeon)') || (c.trait || '').includes('(Long-Range Weapon)'))
    ).length;
  },
});

// ─── GD06-018 · Elpeo Ple's Qubeley Mk-II (Lv3) — Unit ──────────────────────
// <Breach 1> (native keyword — no code needed)
// [During Pair][Activate･Action][Once per Turn] Choose 1 of your (Neo
// Zeon)/(Long-Range Weapon) Units or 1 of your (Neo Zeon) Bases. During this
// battle, battle damage this Unit would receive is dealt to that Unit/Base
// instead.
// Unit [Activate･Action] convention (canAction/action + once-per-turn via
// activateActionUsedThisTurn — see getActionEligibleUnits in game.js), only
// offered while actually battling (ctx.battle, this Unit as either attacker
// or defender). Redirect mechanism reuses two already-proven mechanisms:
// - As DEFENDER: pushes a BDProxy_<targetUid> tempKeyword onto itself — same
//   convention runDamageStep already reads for GD04-095 Lunamaria Hawke,
//   just protecting itself instead of a chosen OTHER Unit.
// - As ATTACKER: sets state.attackerBdProxyUid (GD04-087 Elan Ceres's own
//   counter-damage-taken redirect), since a Unit that's attacking only ever
//   takes damage back as counter-damage, never incoming attack damage.
// NOT YET SUPPORTED: choosing a (Neo Zeon) Base as the redirect target.
// Both proxy mechanisms above only ever resolve against a field Unit —
// extending them to a Base target means duplicating a large chunk of
// runDamageStep's lethal-damage/destroy-handling branch for a Base-shaped
// recipient, which deserves its own careful, separately-tested pass rather
// than folding into this batch. Only (Neo Zeon)/(Long-Range Weapon) Units
// are offered as candidates for now.
function gd06018UnitCandidates(card) {
  return state.playerField.filter(c =>
    c && c.uid !== card.uid && ((c.trait || '').includes('(Neo Zeon)') || (c.trait || '').includes('(Long-Range Weapon)'))
  );
}
function gd06018InBattle(card, battle) {
  return !!battle && (battle.attacker?.uid === card.uid || battle.defender?.uid === card.uid);
}
registerCardEffect('GD06-018', {
  canAction: (card, ctx) => !!card.pilot && gd06018InBattle(card, ctx?.battle) && gd06018UnitCandidates(card).length > 0,
  action: (card, ctx) => {
    const battle = ctx?.battle;
    if (!card.pilot || !gd06018InBattle(card, battle)) { ctx.onDone?.(); return; }
    const candidates = gd06018UnitCandidates(card);
    if (candidates.length === 0) {
      addLog(`${card.name} — no other (Neo Zeon)/(Long-Range Weapon) Unit to redirect to. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Neo Zeon)/(Long-Range Weapon) Units. Battle damage this Unit would receive this battle is dealt to that Unit instead.',
      candidates,
      (target) => {
        if (target) {
          const isAttacking = battle.attacker?.uid === card.uid;
          if (isAttacking) {
            state.attackerBdProxyUid = target.uid;
            NET.send({ type: 'SET_ATTACKER_PROXY', uid: target.uid });
          } else {
            const proxyKw = `BDProxy_${target.uid}`;
            card.tempKeywords = card.tempKeywords || [];
            if (!card.tempKeywords.some(kw => kw.startsWith('BDProxy_'))) card.tempKeywords.push(proxyKw);
            NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: proxyKw });
          }
          addLog(`${card.name} — battle damage it would receive this battle will be redirected to ${target.name} instead.`);
        }
        ctx.onDone?.();
      },
      card,
      { mandatory: false, skipLabel: 'Decline' }
    );
  },
});

// ─── GD06-020 · Hamma-Hamma — Unit ─────────────────────────────────────────
// During your turn, when one of your (Neo Zeon) Units destroys an enemy
// card with damage, this Unit recovers 1 HP.
// "an enemy card" ~= "an enemy Unit" here, same as GD05-006 Hashmal/GD02-002
// Gundam Epyon's identical phrasing — onAnyFriendlyUnitDestroysEnemy/
// onUnitDestroysEnemy both only ever fire from notifyUnitDestroyedByBattle's
// Unit-kill branch (never for a shield/base kill — that's the separate
// onAttackDestroysShield hook family). Both hooks are needed because
// onAnyFriendlyUnitDestroysEnemy explicitly skips the destroyer itself (see
// game.js) — this covers both "some OTHER (Neo Zeon) Unit destroyed it" and
// "Hamma-Hamma destroyed it itself".
function gd06020HealHook(card, destroyer, destroyerSide) {
  if (destroyerSide !== 'player' || !state.myTurn) return;
  if (!(destroyer.trait || '').includes('(Neo Zeon)')) return;
  applyHpMod([card], 1);
  addLog(`${card.name} — recovered 1 HP ((Neo Zeon) Unit destroyed an enemy card).`);
  render(); setupDropZones();
}
registerCardEffect('GD06-020', {
  onAnyFriendlyUnitDestroysEnemy: (card, destroyer, _destroyed, destroyerSide) =>
    gd06020HealHook(card, destroyer, destroyerSide),
  onUnitDestroysEnemy: (card, _destroyed, destroyerSide) =>
    gd06020HealHook(card, card, destroyerSide),
});

// ─── GD06-086 · Mashymre Cello — Pilot ──────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// When playing this card from your hand and pairing it with a Unit with the
// [Mashymre Cello] link condition, it gets cost -1.
// pilotCostOverride is checked in PAIR_PILOT (game.js) before spendResources
// is called — same hook as GD03-085 Christina Mackenzie, but checked via the
// generic isLinkUnit() link-condition match instead of a hardcoded name
// string, since the printed condition here is a link requirement (e.g.
// GD06-020 Hamma-Hamma's "[Mashymre Cello]" link), not a name substring.
// handCostReduction feeds the same hand-card "cost 0 + ↓↓ badge" preview
// ST08-001 Ξ Gundam uses (effectiveHandCost/buildCardEl's _handModified,
// game.js) — it can only report "a discount is available" from board state
// alone (the hand UI doesn't know which Unit will actually be chosen), so it
// fires whenever ANY eligible Unit is in play; pilotCostOverride above still
// decides the REAL charged cost once a specific Unit is actually chosen.
registerCardEffect('GD06-086', {
  handCostReduction: (pilot) =>
    state.playerField.some(c => c && isLinkUnit({ ...c, pilot })) ? 1 : 0,
  pilotCostOverride: (pilot, unitCard) => {
    const base = parseInt(pilot.cost) || 0;
    return isLinkUnit({ ...unitCard, pilot }) ? Math.max(0, base - 1) : base;
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 follow-up — newly added cards
// ════════════════════════════════════════════════════════════════════════════

// ─── GD06-123 · Endra — Base ────────────────────────────────────────────────
// [Burst] Deploy this card and [Deploy] Add 1 of your Shields to your hand
// are handled generically by deployBaseFromShield()/applyBaseDeployEffect()
// in js/game.js. Handler below only covers the extra conditional clause.
// [Deploy] Then, if it is your turn and you have a (Neo Zeon) Unit paired
// with a Pilot that is Lv.3 or lower in play, choose 1 enemy Unit with
// <Blocker> and 3 or less AP. Destroy it.
// Note: this cardNo previously collided with a second, mis-numbered DB entry
// ("Argama", a red Base) — fixed to its correct number GD06-125 in the DB
// (see red.js's GD06-125 registration) so both cards can be registered.
function gd06123HasQualifyingPair() {
  return state.myTurn && state.playerField.some(c =>
    c && (c.trait || '').includes('(Neo Zeon)') && c.pilot && (parseInt(c.pilot.level) || 0) <= 3
  );
}
function gd06123Candidates() {
  return state.oppField.filter(c => c && hasNativeKeyword(c, 'Blocker') && effectiveBp(c) <= 3);
}
registerCardEffect('GD06-123', {
  deploy: (card) => {
    if (!gd06123HasQualifyingPair()) {
      addLog(`${card.name} — [Deploy] condition not met (needs your turn + a friendly (Neo Zeon) Unit paired with a Lv.3-or-lower Pilot in play). No further effect.`);
      return;
    }
    const candidates = gd06123Candidates();
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with <Blocker> and 3 or less AP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with <Blocker> and 3 or less AP. Destroy it.', candidates, (target) => {
      if (target) effectDestroyEnemyUnit(target);
    }, card, { mandatory: true });
  },
});

// ─── GD06-106 · Presence Detected — Command ─────────────────────────────────
// [Action] During this battle, your shield area cards can't receive damage
// from enemy Units with 6 or less AP.
// Same state.battleShieldImmuneAp mechanism as ST02-013 Peaceful Timbre /
// GD02-106 White Wolf's Lv-based state.battleShieldImmuneLv (see
// runDamageStep in game.js) — just keyed on AP instead of Lv.
registerCardEffect('GD06-106', {
  action: (card, ctx) => {
    state.battleShieldImmuneAp = Math.max(state.battleShieldImmuneAp || 0, 6);
    addLog(`${card.name} — shields are immune to damage from Units with 6 or less AP this battle.`);
    ctx.onDone?.();
  },
});

// ─── GD06-108 · On Edge — Command Pilot ─────────────────────────────────────
// [Main] All friendly (Neo Zeon) Units that are Lv.4 or lower get AP+2
// during this turn.
// [Pilot] [Chara Soon]
registerCardEffect('GD06-108', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('(Neo Zeon)') && (parseInt(c.level) || 0) <= 4),
  main: (card, ctx) => {
    const targets = state.playerField.filter(c => c && (c.trait || '').includes('(Neo Zeon)') && (parseInt(c.level) || 0) <= 4);
    if (targets.length === 0) {
      addLog(`${card.name} — no friendly (Neo Zeon) Units Lv.4 or lower in play. No effect.`);
      ctx.onDone(); return;
    }
    applyApMod(targets, 2, 'tempApMod');
    addLog(`${card.name} — all friendly (Neo Zeon) Units Lv.4 or lower get AP+2 during this turn.`);
    render();
    ctx.onDone();
  },
});

// ─── GD06-023 · Galluss J — Unit ───────────────────────────────────────────
// [Deploy] Rest this Unit. Then, choose 1 of your (Neo Zeon) Units that is
// Lv.3 or higher. It gets AP+1 during this turn. If you increase a Link
// Unit's AP with this effect, set this Unit as active.
registerCardEffect('GD06-023', {
  deploy: (card) => {
    setRested(card, true);
    addLog(`${card.name} — [Deploy] rested this Unit.`);
    const candidates = state.playerField.filter(c =>
      c && (c.trait || '').includes('(Neo Zeon)') && (parseInt(c.level) || 0) >= 3
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Neo Zeon) Units Lv.3 or higher. No effect.`);
      render(); setupDropZones();
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Neo Zeon) Units that is Lv.3 or higher. It gets AP+1 during this turn.',
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], 1, 'tempApMod');
          addLog(`${card.name} — ${target.name} gets AP+1 during this turn.`);
          if (isLinkUnit(target)) {
            setRested(card, false);
            addLog(`${card.name} — increased a Link Unit's AP: set active.`);
          }
        }
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-027 · Mashymre Zssa — Unit ────────────────────────────────────────
// [Deploy] Choose 1 of your (Neo Zeon) Units. During this turn, it may choose
// an active enemy Unit with 4 or less AP as its attack target.
// CanAttackActiveAP4 tempKeyword — same convention as GD05-028 Kayra's Jegan.
registerCardEffect('GD06-027', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && (c.trait || '').includes('(Neo Zeon)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly (Neo Zeon) Units. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 of your (Neo Zeon) Units. It may attack active enemy Units with 4 or less AP this turn.',
      candidates,
      (target) => {
        if (!target) return;
        target.tempKeywords = target.tempKeywords || [];
        if (!target.tempKeywords.includes('CanAttackActiveAP4')) target.tempKeywords.push('CanAttackActiveAP4');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: target.uid, keyword: 'CanAttackActiveAP4' });
        addLog(`${card.name} — ${target.name} can attack active enemy Units with 4 or less AP this turn.`);
        render();
        setupDropZones();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD06-030 · Mahiroo — Unit ──────────────────────────────────────────────
// While you have 3 or more other (Ghingnham Fleet)/(Long-Range Weapon) Units
// in play, this Unit gets AP+2. Live-evaluated every AP read, same
// convention as GD06-060 Zaku II F2's trash-count check.
registerCardEffect('GD06-030', {
  passiveUnitApBonus: (card, ownerSide) => {
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const count = field.filter(c =>
      c && c.uid !== card.uid && ((c.trait || '').includes('(Ghingnham Fleet)') || (c.trait || '').includes('(Long-Range Weapon)'))
    ).length;
    return count >= 3 ? 2 : 0;
  },
});

// ─── GD06-032 · Bellona — Unit ──────────────────────────────────────────────
// [Destroyed] Look at the top 2 cards of your deck. You may reveal 1
// (Ghingnham Fleet)/(Dianna Counter) Unit card among them and add it to your
// hand. Return the remaining cards randomly to the bottom of your deck.
registerCardEffect('GD06-032', {
  destroyed: (card, ctx) => {
    if (ctx?.ownerSide !== 'player') return;
    lookAtTopAndPickToHand(
      card, 2,
      'Look at top 2 cards. Reveal 1 (Ghingnham Fleet)/(Dianna Counter) Unit card to add to your hand.',
      c => (c.type === 'unit' || c.type === 'unit-token') &&
           ((c.trait || '').includes('(Ghingnham Fleet)') || (c.trait || '').includes('(Dianna Counter)'))
    );
  },
});
